/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Typography, Input, Row, Col, Select, Spin, message, Modal, Upload, Checkbox as AntCheckbox } from 'antd';
import { BarChartOutlined, PlusOutlined, DeleteOutlined, CheckOutlined, DownloadOutlined, UploadOutlined, EditOutlined } from '@ant-design/icons';
import { BarChart2, Plus, X, ArrowRight, CheckCircle, AlertCircle, XCircle, Trash2, Edit, ChevronDown, ChevronUp, Search, Save, Wrench, Scissors } from 'lucide-react';
import { Button as CrcButton, Checkbox, Header, Toggle, Spinner, ConfirmationModal } from '@crc-evans/ui-components';
import ExcelJS from 'exceljs';
import * as XLSX from 'xlsx';
import axiosConfig from '../../services/axiosConfig';
import { useDispatch, useSelector } from 'react-redux';
import { projectNdtTypesArray } from '../../utils/sowNdtStation';
import { updateDprSkipState } from '../../redux/dpr/action';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const statusToInternal = (raw) => {
  const v = String(raw || '').trim().toLowerCase();
  if (!v) return 'accept';
  if (v === 'accept' || v === 'accepted') return 'accept';
  if (v === 'acceptwithindication' || v === 'accepted with indication' || v === 'accept w/ indication') return 'indication';
  if (v.includes('reject') && v.includes('cut')) return 'reject_cutout';
  if (v.includes('reject') && v.includes('repair')) return 'reject_repair';
  if (v === 'reject') return 'reject';
  if (v === 'indication') return 'indication';
  return v;
};

const statusToApiLabel = (internal) => {
  const v = String(internal || '').trim().toLowerCase();
  if (v === 'accept') return 'Accepted';
  if (v === 'indication') return 'Accepted with Indication';
  if (v === 'reject_cutout') return 'Rejected (Cutout)';
  if (v === 'reject_repair') return 'Rejected (Repair)';
  // legacy fallbacks
  if (v === 'reject') return 'Rejected (Repair)';
  return 'Accepted';
};

const isAutFamily = (ndtCode) => {
  const v = String(ndtCode || '').trim().toUpperCase();
  return v === 'AUT' || v === 'PAUT';
};

// Productivity is project-level for a DPR log; avoid refetching per-mainline panels.
const __dprProductivityCache = new Map(); // key: `${dprLogId}|${shift}` -> { data?, promise? }
const __dprPipeTallyCache = new Map(); // key: `${dprLogId}|${shift}` -> { data?, promise? }
const __dprPersonnelCache = new Map(); // key: `${dprLogId}|${shift}` -> { data?, promise? }

const getCacheKey = (dprLogId, shift) => `${String(dprLogId || '')}|${String(shift || '')}`;

/** Clear module-level GET caches for a log + shift (e.g. when re-opening the Productivity tab). Next fetch bypasses stale cache. */
export function invalidateDprProductivityCachesForLog(dprLogId, shiftVal) {
  if (dprLogId == null || String(dprLogId).trim() === '') return;
  const cacheKey = getCacheKey(dprLogId, shiftVal);
  __dprProductivityCache.delete(cacheKey);
  __dprPipeTallyCache.delete(cacheKey);
  __dprPersonnelCache.delete(cacheKey);
}

/**
 * Call when the user opens the Productivity tab: bust caches and GET `/dpr-productivity`
 * (same cache key as panels so follow-up `reloadProductivityData` reuses this response).
 */
export async function fetchDprProductivityLogWhenTabOpens(dprLogId, shiftVal) {
  if (dprLogId == null || String(dprLogId).trim() === '') return null;
  const cacheKey = getCacheKey(dprLogId, shiftVal);
  invalidateDprProductivityCachesForLog(dprLogId, shiftVal);
  return getOrFetch(__dprProductivityCache, cacheKey, () =>
    axiosConfig.get(`/dpr-productivity?dprLogId=${dprLogId}&shift=${shiftVal}`)
  );
}

async function getOrFetch(cache, key, fetcher) {
  const existing = cache.get(key);
  if (existing?.data) return existing.data;
  if (existing?.promise) return existing.promise;
  const p = (async () => {
    const data = await fetcher();
    cache.set(key, { data });
    return data;
  })().catch((err) => {
    cache.delete(key);
    throw err;
  });
  cache.set(key, { promise: p });
  return p;
}

/** Single welder id as trimmed string; supports API scalar or stamped fields (not personnel row `id`). */
const welderIdFromUnknown = (raw) => {
  if (raw == null || raw === '') return '';
  if (typeof raw === 'object') {
    const id =
      raw.welderId ??
      raw.welder_id ??
      raw.welderID ??
      raw.welderStamp ??
      raw.welder_stamp ??
      raw.stampNumber ??
      raw.stamp ??
      raw.userId ??
      raw.user_id;
    return id != null && String(id).trim() !== '' ? String(id).trim() : '';
  }
  return String(raw).trim();
};

/** Personnel / welderPersonnel rows shaped like DPR personnel API (role + optional category). */
const isWelderPersonnelEntry = (raw) => {
  if (raw == null || typeof raw !== 'object') return false;
  const rn = raw?.role?.roleName ?? raw.roleName ?? '';
  const cat = raw.category ?? '';
  return /^welder$/i.test(String(rn).trim()) || String(cat).toLowerCase() === 'welder';
};

/** Normalize welder id list from weld metrics / row state into string[]. */
const normalizeWelderIdsFromMetric = (value) => {
  if (value == null || value === '') return [];
  if (Array.isArray(value)) {
    return value.map(welderIdFromUnknown).filter(Boolean);
  }
  const one = welderIdFromUnknown(value);
  return one ? [one] : [];
};

const truthyWeldMetric = (v) => v === 1 || v === '1' || v === true;

const spreadsheetCellProvided = (v) => v !== undefined && v !== null && v !== '';

/** First non-empty spreadsheet cell → boolean (1/yes/true). */
const spreadsheetTruthyPick = (...vals) => {
  for (const v of vals) {
    if (!spreadsheetCellProvided(v)) continue;
    return truthyWeldMetric(v) || /^yes$/i.test(String(v).trim());
  }
  return false;
};

const summaryAsNumber = (raw) => {
  const n = parseInt(String(raw ?? '').replace(/\D/g, ''), 10);
  return Number.isFinite(n) ? n : 0;
};

const weldRowMergeKey = (row) =>
  `${String(row?.jointNo || '').trim().toLowerCase()}|${String(row?.ndtMethod || row?.ndtType || '').trim().toUpperCase()}`;

/** API defectInfo (nested metrics rows or flat objects) → edit-form indications[]. */
const indicationsFromDefectInfo = (value) => {
  if (!Array.isArray(value) || value.length === 0) return [];
  return value.map((defectItem) => {
    if (Array.isArray(defectItem)) {
      const indication = {
        defectType: '',
        start: '',
        end: '',
        depth: '',
        length: '',
        defectAccepted: true,
        height: ''
      };
      defectItem.forEach((field) => {
        if (field?.keyName === 'defectType') indication.defectType = field.value || '';
        if (field?.keyName === 'startLoc') indication.start = field.value || '';
        if (field?.keyName === 'endLoc') indication.end = field.value || '';
        if (field?.keyName === 'depth') indication.depth = field.value || '';
        if (field?.keyName === 'length') indication.length = field.value || '';
        if (field?.keyName === 'defectAccepted') {
          indication.defectAccepted =
            field.value === 1 || field.value === '1' || field.value === true || field.value === 'true';
        }
        if (field?.keyName === 'height') indication.height = field.value || '';
      });
      return indication;
    }
    return {
      defectType: defectItem?.defectType ?? '',
      start: defectItem?.startLoc ?? defectItem?.start ?? '',
      end: defectItem?.endLoc ?? defectItem?.end ?? '',
      depth: defectItem?.depth ?? '',
      length: defectItem?.length ?? '',
      height: defectItem?.height ?? '',
      defectAccepted:
        defectItem?.defectAccepted === true ||
        defectItem?.defectAccepted === 1 ||
        defectItem?.defectAccepted === '1'
    };
  });
};

/** Edit-form indications[] → flat defectInfo on dailyWeldLog rows. */
const defectInfoFlatFromIndications = (indications) => {
  if (!Array.isArray(indications)) return [];
  return indications.map((ind) => ({
    defectType: ind?.defectType ?? '',
    startLoc: ind?.start ?? '',
    endLoc: ind?.end ?? '',
    depth: ind?.depth ?? '',
    length: ind?.length ?? '',
    height: ind?.height ?? '',
    defectAccepted:
      ind?.defectAccepted === true || ind?.defectAccepted === 1 || ind?.defectAccepted === '1' ? 1 : 0
  }));
};

const indicationHasData = (ind) => {
  if (!ind || typeof ind !== 'object') return false;
  return !!(
    String(ind.defectType || '').trim() ||
    String(ind.start || '').trim() ||
    String(ind.end || '').trim() ||
    String(ind.depth || '').trim() ||
    String(ind.length || '').trim() ||
    String(ind.height || '').trim()
  );
};

/** Parse weldLogMetrics row for indication + comment fields (merge into dailyWeldLog UI rows). */
const parseRichFieldsFromWeldLogMetricsRow = (metricsRow) => {
  const row = {};
  (Array.isArray(metricsRow) ? metricsRow : []).forEach((metric) => {
    if (!metric?.keyName || metric.keyName === 'id') return;
    const { keyName, value } = metric;
    if (keyName === 'defectInfo') {
      row.indications = indicationsFromDefectInfo(value);
    } else if (keyName === 'Customercomments' || keyName === 'comments') {
      row.comments = value || '';
    } else if (keyName === 'crceComments' || keyName === 'crceComment') {
      row.crceComment = value || '';
    } else if (keyName === 'repairComments' || keyName === 'repairComment') {
      row.repairComment = value || '';
    } else if (keyName === 'jointNo' || keyName === 'Joint No') {
      row.jointNo = value || '';
    } else if (keyName === 'ndtMethod' || keyName === 'NDT Method') {
      row.ndtMethod = String(value || '').trim().toUpperCase();
    }
  });
  return row;
};

const mergeRichFieldsFromWeldLogMetrics = (dailyRows, weldLogMetricsData) => {
  if (!Array.isArray(dailyRows) || dailyRows.length === 0) return dailyRows;
  if (!Array.isArray(weldLogMetricsData) || weldLogMetricsData.length === 0) return dailyRows;

  const richByKey = new Map();
  weldLogMetricsData.forEach((metricsRow) => {
    const rich = parseRichFieldsFromWeldLogMetricsRow(metricsRow);
    if (!rich.jointNo) return;
    richByKey.set(weldRowMergeKey(rich), rich);
  });

  return dailyRows.map((row) => {
    const rich = richByKey.get(weldRowMergeKey(row));
    if (!rich) return row;
    return {
      ...row,
      ...(Array.isArray(rich.indications) && rich.indications.length > 0
        ? { indications: rich.indications }
        : {}),
      ...(rich.comments != null && rich.comments !== '' ? { comments: rich.comments } : {}),
      ...(rich.crceComment != null && rich.crceComment !== '' ? { crceComment: rich.crceComment } : {}),
      ...(rich.repairComment != null && rich.repairComment !== '' ? { repairComment: rich.repairComment } : {})
    };
  });
};

/**
 * PUT `dailyWeldLog` row: shared fields + exactly one trio of counters —
 * RT (default / unknown): rtAcc, rtReject, rtCutOut
 * AUT: autAcc, autReject, autCutOut
 * PAUT: pautAcc, pautReject, pautCutOut
 * MUT: mutAcc, mutReject, mutCutOut
 */
const buildDailyWeldLogPayloadRow = (r, defaultNdtFallback) => {
  const ndtType = String(r.ndtMethod || '').trim().toUpperCase() || String(defaultNdtFallback || 'RT').trim().toUpperCase();
  const st = String(r.status || '').trim().toLowerCase();
  const isAcc = st === 'accept' || st === 'indication';
  const isCut = st === 'reject_cutout';
  const isRepair = st === 'reject_repair' || st === 'reject';

  const acc = isAcc ? 1 : 0;
  const rej = (isCut || isRepair) ? 1 : 0;
  const cut = isCut ? 1 : 0;

  const defectInfo = defectInfoFlatFromIndications(r.indications).filter(indicationHasData);

  const base = {
    date: r.date || new Date().toISOString().split('T')[0],
    jointNo: String(r.jointNo || '').trim(),
    ndtType,
    welderId: Array.isArray(r.welderId) ? r.welderId : normalizeWelderIdsFromMetric(r.welderId),
    status: statusToApiLabel(r.status),
    repair: isRepair ? 1 : 0,
    comments: r.comments != null ? String(r.comments) : '',
    crceComment: r.crceComment != null ? String(r.crceComment) : '',
    repairComment: r.repairComment != null ? String(r.repairComment) : '',
    ...(defectInfo.length > 0 ? { defectInfo } : {})
  };

  switch (ndtType) {
    case 'AUT':
      return { ...base, autAcc: acc, autReject: rej, autCutOut: cut };
    case 'PAUT':
      return { ...base, pautAcc: acc, pautReject: rej, pautCutOut: cut };
    case 'MUT':
      return { ...base, mutAcc: acc, mutReject: rej, mutCutOut: cut };
    default:
      return { ...base, rtAcc: acc, rtReject: rej, rtCutOut: cut };
  }
};

/** Map GET dailyWeldLog counters → internal rt/aut toggles used by Edit weld UI. */
const uiMetricsFromDailyWeldLogEntry = (e, ndtMethod) => {
  const n = String(ndtMethod || '').trim().toUpperCase();
  let rtAccepted = false;
  let autAccepted = false;
  let rtCutOut = false;
  let autCutOut = false;

  switch (n) {
    case 'RT':
      rtAccepted = truthyWeldMetric(e.rtAcc);
      rtCutOut = truthyWeldMetric(e.rtCutOut);
      break;
    case 'AUT':
      autAccepted = truthyWeldMetric(e.autAcc);
      autCutOut = truthyWeldMetric(e.autCutOut);
      break;
    case 'MUT':
      rtAccepted = truthyWeldMetric(e.mutAcc);
      rtCutOut = truthyWeldMetric(e.mutCutOut);
      break;
    case 'PAUT':
      autAccepted = truthyWeldMetric(e.pautAcc);
      autCutOut = truthyWeldMetric(e.pautCutOut);
      break;
    default:
      rtAccepted = truthyWeldMetric(e.rtAcc);
      rtCutOut = truthyWeldMetric(e.rtCutOut);
      break;
  }

  return { rtAccepted, autAccepted, rtCutOut, autCutOut };
};

/** Normalize productivity API welderPersonnel for dropdowns (stable string welderId; optional name for display). */
const normalizeWelderPersonnelList = (list) => {
  if (!Array.isArray(list)) return [];
  const out = [];
  for (const raw of list) {
    if (raw == null || typeof raw !== 'object') continue;
    let welderId = welderIdFromUnknown(raw);
    let welderStampPending = false;
    // DPR personnel often has role Welder but welderId null until set on user/team — still show in productivity picker
    if (!welderId && isWelderPersonnelEntry(raw) && raw.id != null && String(raw.id).trim() !== '') {
      welderId = `dpr-personnel-${String(raw.id).trim()}`;
      welderStampPending = true;
    }
    const displayName = String(
      raw.fullName ??
        raw.full_name ??
        raw.name ??
        raw.personnelName ??
        raw.userName ??
        ''
    ).trim();
    if (!welderId) continue;
    out.push({ ...raw, welderId, displayName, welderStampPending });
  }
  return out;
};

/** Parse GET /dpr-personnel response the same way as PersonnelLog (nested shapes). */
const extractPersonnelEntriesFromDprResponse = (response) => {
  const d = response?.data;
  if (!d) return [];
  if (Array.isArray(d.personnelEntries)) return d.personnelEntries;
  if (d.data && Array.isArray(d.data.personnelEntries)) return d.data.personnelEntries;
  if (Array.isArray(d.data)) return d.data;
  return [];
};

/**
 * Productivity API often returns project-level welderPersonnel only.
 * DPR day/night personnel (incl. new welders) lives on /dpr-personnel — merge so the dropdown stays in sync.
 */
const mergeWelderPersonnelFromProductivityAndDpr = (productivityWelderList, dprPersonnelEntries) => {
  const primary = normalizeWelderPersonnelList(
    Array.isArray(productivityWelderList) ? productivityWelderList : []
  );
  const seen = new Set(primary.map((o) => String(o.welderId ?? '').trim()).filter(Boolean));
  const welderOnlyRows = Array.isArray(dprPersonnelEntries)
    ? dprPersonnelEntries.filter(isWelderPersonnelEntry)
    : [];
  const secondary = normalizeWelderPersonnelList(welderOnlyRows);
  const merged = [...primary];
  for (const opt of secondary) {
    const k = String(opt.welderId ?? '').trim();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    merged.push(opt);
  }
  return merged;
};

/** Row statuses that still serialize to API status Reject + rtReject/autReject = 1 */
const isRejectStatus = (status) =>
  status === 'reject' || status === 'reject_repair' || status === 'reject_cutout';

/**
 * Keep repair / cut-out flags aligned with the weld status the user picked (RT vs AUT uses the right cut-out field).
 */
const weldRowStatusSideEffects = (row, status) => {
  const base = { ...row, status };
  const isAut = isAutFamily(row.ndtMethod);
  if (status === 'accept' || status === 'indication') {
    return {
      ...base,
      repair: false,
      rtCutOut: false,
      autCutOut: false,
      stn1CutOut: false
    };
  }
  if (status === 'reject_repair') {
    return {
      ...base,
      repair: true,
      rtCutOut: false,
      autCutOut: false,
      stn1CutOut: false
    };
  }
  if (status === 'reject_cutout') {
    return {
      ...base,
      repair: false,
      rtCutOut: !isAut,
      autCutOut: isAut,
      stn1CutOut: false
    };
  }
  if (status === 'reject') {
    return base;
  }
  return base;
};

const CustomWelderSelect = ({ value, options, onChange, placeholder = "Select Welders", onClose }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);

  const selectedIds = useMemo(() => normalizeWelderIdsFromMetric(
    Array.isArray(value) ? value : (value != null && value !== '' ? [value] : [])
  ), [value]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        if (isOpen && typeof onClose === 'function') {
          onClose(selectedIds);
        }
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, selectedIds]);

  const filteredOptions = useMemo(() => {
    const q = (searchTerm || '').toLowerCase().trim();
    return (options || []).filter((opt) => {
      const id = String(opt.welderId ?? '').toLowerCase();
      const name = String(opt.displayName ?? opt.fullName ?? opt.full_name ?? '').toLowerCase();
      if (!q) return true;
      return id.includes(q) || name.includes(q);
    });
  }, [options, searchTerm]);

  const toggleValue = (rawVal) => {
    const id = welderIdFromUnknown(rawVal);
    if (!id) return;
    let newValues;
    if (selectedSet.has(id)) {
      newValues = selectedIds.filter((v) => v !== id);
    } else {
      newValues = [...selectedIds, id];
    }
    onChange(newValues);
  };

  const optionByWelderId = useMemo(() => {
    const m = new Map();
    (options || []).forEach((o) => {
      const k = String(o.welderId ?? '').trim();
      if (k) m.set(k, o);
    });
    return m;
  }, [options]);

  const chipLabel = (val) => {
    const opt = optionByWelderId.get(String(val).trim());
    if (opt?.welderStampPending && opt.displayName) return opt.displayName;
    return val;
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', textAlign: 'left' }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          minHeight: '42px',
          padding: '4px 12px',
          borderRadius: '8px',
          border: isOpen ? '2px solid #3B82F6' : '1px solid #D1D5DB',
          backgroundColor: '#fff',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
          transition: 'all 0.2s',
          boxShadow: isOpen ? '0 0 0 4px rgba(59, 130, 246, 0.1)' : 'none'
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', flex: 1, overflow: 'hidden' }}>
          {selectedIds.length > 0 ? (
            selectedIds.map(val => (
              <span
                key={val}
                style={{
                  backgroundColor: '#EFF6FF',
                  color: '#1E40AF',
                  fontSize: '12px',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  border: '1px solid #DBEAFE',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                {chipLabel(val)}
                <X
                  size={12}
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleValue(val);
                  }}
                />
              </span>
            ))
          ) : (
            <span style={{ color: '#9CA3AF', fontSize: '14px' }}>{placeholder}</span>
          )}
        </div>
        {isOpen ? <ChevronUp size={18} color="#3B82F6" /> : <ChevronDown size={18} color="#6B7280" />}
      </div>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          animation: 'fadeIn 0.2s ease-out'
        }}>
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; transform: translateY(-8px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>

          <div style={{ position: 'relative' }}>
            <input
              autoFocus
              placeholder="Search..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                height: '40px',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                fontSize: '14px',
                outline: 'none',
                color: '#1F2937',
                boxSizing: 'border-box',
                backgroundColor: '#fff'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3B82F6'}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
            />
          </div>

          <div style={{ maxHeight: '240px', overflowY: 'auto', paddingRight: '4px' }}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map(opt => {
                const optId = String(opt.welderId ?? '').trim();
                const checked = optId !== '' && selectedSet.has(optId);
                const rowLabel =
                  opt.welderStampPending && opt.displayName
                    ? `${opt.displayName} (no welder ID — set in Personnel / Team)`
                    : opt.displayName
                      ? `${opt.welderId} — ${opt.displayName}`
                      : opt.welderId;
                return (
                <div
                  key={optId || JSON.stringify(opt)}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleValue(opt.welderId);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    transition: 'all 0.1s',
                    marginBottom: '4px',
                    backgroundColor: checked ? '#F3F4F6' : 'transparent'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = checked ? '#F3F4F6' : 'transparent'}
                >
                  <div style={{
                    width: '18px',
                    height: '18px',
                    border: checked ? 'none' : '1px solid #D1D5DB',
                    borderRadius: '4px',
                    backgroundColor: checked ? '#3B82F6' : 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '12px',
                    transition: 'all 0.1s'
                  }}>
                    {checked && (
                      <CheckOutlined style={{ color: 'white', fontSize: '10px' }} />
                    )}
                  </div>
                  <span style={{
                    fontSize: '14px',
                    color: '#374151',
                    fontWeight: checked ? 500 : 400
                  }}>
                    {rowLabel}
                  </span>
                </div>
                );
              })
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: '#9CA3AF', fontSize: '14px' }}>
                No welders found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Bulk status dropdown – same trigger styling as CustomWelderSelect (header row)
const BulkStatusSelect = ({ onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const triggerStyle = {
    minHeight: '42px',
    padding: '4px 12px',
    borderRadius: '8px',
    border: isOpen ? '2px solid #3B82F6' : '1px solid #D1D5DB',
    backgroundColor: '#fff',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
    boxShadow: isOpen ? '0 0 0 4px rgba(59, 130, 246, 0.1)' : 'none',
    minWidth: '180px'
  };

  const options = [
    { value: 'accept', label: 'Accept' },
    { value: 'indication', label: 'Accept w/ Indication' },
    { value: 'reject_repair', label: 'Rejected (Repair)', Icon: Wrench },
    { value: 'reject_cutout', label: 'Rejected (Cutout)', Icon: Scissors }
  ];

  const handleChoose = (value) => {
    onSelect(value);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', textAlign: 'left' }}>
      <div onClick={() => setIsOpen(!isOpen)} style={triggerStyle}>
        <span style={{ color: '#9CA3AF', fontSize: '14px' }}>Set status for selected</span>
        {isOpen ? <ChevronUp size={18} color="#3B82F6" /> : <ChevronDown size={18} color="#6B7280" />}
      </div>
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
          padding: '8px 0',
          overflow: 'hidden'
        }}>
          {options.map((opt) => (
            <div
              key={opt.value}
              onClick={() => handleChoose(opt.value)}
              style={{
                padding: '10px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#374151',
                transition: 'background 0.1s'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F9FAFB'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {opt.Icon ? <opt.Icon size={16} color="#374151" aria-hidden /> : null}
                {opt.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Defect Type dropdown – same styling as Quality Root / Downtime Option dropdown
const CustomDefectTypeSelect = ({ value, options = [], onChange, placeholder = 'Select Defect Type' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const opts = options && options.length > 0
    ? options.map(d => ({ code: d.code, label: `${d.code} - ${d.description || d.name || d.code}` }))
    : [
        { code: 'IFS', label: 'IFS - Incomplete Fusion' },
        { code: 'POR', label: 'POR - Porosity' },
        { code: 'SLG', label: 'SLG - Slag Inclusion' }
      ];
  const displayLabel = value ? (opts.find(o => o.code === value)?.label || value) : '';

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', minWidth: 0, textAlign: 'left' }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          minHeight: '38px',
          height: '38px',
          boxSizing: 'border-box',
          padding: '4px 12px',
          fontSize: '13px',
          border: isOpen ? '2px solid #3B82F6' : '1px solid #E5E7EB',
          borderRadius: '6px',
          backgroundColor: '#fff',
          color: displayLabel ? '#374151' : '#9CA3AF',
          outline: 'none',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'all 0.2s',
          overflow: 'hidden'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayLabel || placeholder}
        </span>
        {isOpen ? <ChevronUp size={16} color="#6B7280" /> : <ChevronDown size={16} color="#6B7280" />}
      </div>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          zIndex: 9999,
          backgroundColor: '#fff',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          padding: '8px',
          maxHeight: '200px',
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
        >
          <div
            onClick={() => { onChange(''); setIsOpen(false); }}
            style={{
              padding: '8px 10px',
              fontSize: '13px',
              color: '#374151',
              backgroundColor: !value ? '#F3F4F6' : 'transparent',
              cursor: 'pointer',
              borderRadius: '4px',
              marginBottom: '2px',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = !value ? '#F3F4F6' : 'transparent'}
          >
            {placeholder}
          </div>
          {opts.map((opt) => (
            <div
              key={opt.code}
              onClick={() => { onChange(opt.code); setIsOpen(false); }}
              style={{
                padding: '8px 10px',
                fontSize: '13px',
                color: '#374151',
                backgroundColor: value === opt.code ? '#F3F4F6' : 'transparent',
                cursor: 'pointer',
                borderRadius: '4px',
                marginBottom: '2px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = value === opt.code ? '#F3F4F6' : 'transparent'}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ProductivityMainlinePanel = forwardRef(({
  productivityData,
  onSave,
  isSubmitting = false,
  shift = 'day',
  dprLogId,
  sowId,
  selectedProject,
  selectedPipeline,
  /** Display title for this mainline (used for API `mainlineLabel`). */
  mainlineTitle = '',
  isLastDprSection,
  /** When nested inside a collapse panel, hide the duplicate page title block */
  compactChrome = false,
  /** Multi-mainline view uses one global Save button (avoid per-card save spam). */
  hideFooterActions = false,
  isShiftSkipped = false
}, ref) => {
  const initialProductivityData = productivityData && typeof productivityData === 'object' ? productivityData : {};
  const [rtProgress, setRtProgress] = useState({
    firstRtWeld: '',
    lastRtWeld: '',
    totalRtAcceptedWelds: 0
  });
  const [testTypes, setTestTypes] = useState([]);
  const [weldLogRows, setWeldLogRows] = useState([]);
  const [originalWeldLogMetrics, setOriginalWeldLogMetrics] = useState([]);
  const [deletedRowIndices, setDeletedRowIndices] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [isLoadingPipeTally, setIsLoadingPipeTally] = useState(false);
  const [isLoadingWeldLog, setIsLoadingWeldLog] = useState(false);
  const dispatch = useDispatch();
  const skipProductivity = useSelector(state => {
    if (!sowId) return false;
    return state.dprReducer?.skipStatesBySection?.[sowId]?.skipProductivity || false;
  });
  const [isSaving, setIsSaving] = useState(false);
  const [reportNo, setReportNo] = useState('');
  const [welderConfigs, setWelderConfigs] = useState([]);
  const [welderPersonnel, setWelderPersonnel] = useState([]);
  const [defectTypes, setDefectTypes] = useState([]);
  const [showAddRowInput, setShowAddRowInput] = useState(false);
  const [rowsToAdd, setRowsToAdd] = useState('');
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [uploadFileList, setUploadFileList] = useState([]);

  // Add Productivity Modal State
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [newProductivity, setNewProductivity] = useState({
    firstJoint: '',
    lastJoint: '',
    ndtMethod: 'RT'
  });
  const [validationError, setValidationError] = useState('');



  // Edit View State
  const [editingRow, setEditingRow] = useState(null);

  const [weldEntryRows, setWeldEntryRows] = useState([]);
  const [bulkWelderPending, setBulkWelderPending] = useState([]);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [isSkipConfirmOpen, setIsSkipConfirmOpen] = useState(false);

  /** Editable summary row (kept in sync with computed weld stats; user can override in the inputs). */
  const [summaryFieldInputs, setSummaryFieldInputs] = useState({
    totalWelds: '0',
    acceptedCounts: '0',
    rejectedCutout: '0',
    rejectedRepair: '0'
  });

  /**
   * Server-provided summary counts for the selected mainline.
   * Prefer these for the Summary strip because daily weld-log rows can be partial
   * (e.g. accepted rows only), while API summary includes rejected counts.
   */
  const [serverSummaryCounts, setServerSummaryCounts] = useState(null);

  /** Latest state for parent ref API (avoid stale closure from useImperativeHandle). */
  const panelExposeSnapshotRef = useRef({});

  /** Welder stamps from API only (omit personnel fallback ids) — pre-filled on new welds when stamps exist. */
  const defaultWelderIdsForNewWeldRows = useMemo(
    () =>
      (welderPersonnel || [])
        .filter((w) => !w.welderStampPending)
        .map((w) => w.welderId)
        .filter((id) => id != null && String(id).trim() !== ''),
    [welderPersonnel]
  );

  const handleSkipProductivityChange = (checked) => {
    if (!sowId) return;
    if (checked) {
      setIsSkipConfirmOpen(true);
    } else {
      dispatch(updateDprSkipState(sowId, 'skipProductivity', false));
    }
  };

  const handleConfirmSkipProductivity = () => {
    if (sowId) dispatch(updateDprSkipState(sowId, 'skipProductivity', true));
    setIsSkipConfirmOpen(false);
  };

  const handleProgressTestTypeFieldChange = (testTypeCode, field, rawValue) => {
    const code = String(testTypeCode || '').toUpperCase();
    setTestTypes((prev) => {
      const list = Array.isArray(prev) ? [...prev] : [];
      const idx = list.findIndex((t) => String(t.testType || '').toUpperCase() === code);
      if (idx === -1) return prev;
      const row = { ...list[idx] };
      if (field === 'firstWeldNo') row.firstWeldNo = rawValue;
      else if (field === 'lastWeldNo') row.lastWeldNo = rawValue;
      else if (field === 'totalCompleted') {
        const n = parseInt(String(rawValue).replace(/\D/g, ''), 10);
        row.totalCompleted = Number.isFinite(n) ? n : 0;
      }
      list[idx] = row;
      return list;
    });
    if (code === 'RT' && field !== 'totalCompleted') {
      setRtProgress((rp) => ({
        ...rp,
        ...(field === 'firstWeldNo' ? { firstRtWeld: rawValue } : {}),
        ...(field === 'lastWeldNo' ? { lastRtWeld: rawValue } : {})
      }));
    }
    if (code === 'RT' && field === 'totalCompleted') {
      const n = parseInt(String(rawValue).replace(/\D/g, ''), 10);
      setRtProgress((rp) => ({
        ...rp,
        totalRtAcceptedWelds: Number.isFinite(n) ? n : 0
      }));
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Daily Weld Log');
      const safeColumns = Array.isArray(columns) ? columns : [];
      const headerTitles = safeColumns
        .map((col) => {
          let title = '';
          if (typeof col.title === 'string') {
            title = col.title;
          } else if (col.title && col.title.props && col.title.props.children) {
            title = col.title.props.children;
          }
          return { title: String(title || '').trim(), dataIndex: col.dataIndex };
        })
        .filter((col) => col.dataIndex && col.title && col.title !== '#' && col.title.toLowerCase() !== 'action')
        .map((col) => col.title);

      worksheet.addRow(headerTitles);
      worksheet.getRow(1).font = { bold: true };
      worksheet.columns = headerTitles.map((title) => ({
        header: title,
        width: Math.max(12, String(title || '').length + 4)
      }));

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'daily_weld_log_template.xlsx';
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Template download error:', error);
      message.error('Failed to download template. Please try again.');
    }
  };

  const handleUploadExcel = () => {
    setIsUploadModalVisible(true);
  };

  const handleUploadModalCancel = () => {
    setIsUploadModalVisible(false);
    setUploadFileList([]);
  };

  const handleUploadExcelFile = async () => {
    if (uploadFileList.length === 0) {
      message.error('Please select an Excel file.');
      return;
    }

    const file = uploadFileList[0].originFileObj || uploadFileList[0];
    if (!file) {
      message.error('Invalid file. Please try again.');
      return;
    }

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (!rows || rows.length < 2) {
        message.error('The uploaded file has no data rows.');
        return;
      }

      const headers = rows[0].map((h) => String(h || '').trim());
      const headerToDataIndex = {};
      const safeColumns = Array.isArray(columns) ? columns : [];
      safeColumns.forEach((col) => {
        if (!col.dataIndex) return;
        const title = typeof col.title === 'string' ? col.title : '';
        if (title) {
          headerToDataIndex[title.toLowerCase()] = col.dataIndex;
        }
      });

      const dataRows = rows.slice(1).filter((row) => row.some((cell) => cell !== null && cell !== undefined && cell !== ''));
      const mappedRows = dataRows.map((row, index) => {
        const rowObj = {
          id: `weld-upload-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`
        };

        headers.forEach((header, colIndex) => {
          if (!header || header === '#') {
            return;
          }
          const dataIndex = headerToDataIndex[header.toLowerCase()];
          if (!dataIndex) return;
          const value = row[colIndex];
          rowObj[dataIndex] = value !== undefined && value !== null ? value : '';
        });

        return rowObj;
      }).map((rowObj) => {
        const raw = rowObj.ndtMethod;
        if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
          const v = String(raw).trim().toUpperCase();
          rowObj.ndtMethod = projectNdtMethods.includes(v) ? v : defaultNdtMethodForNewRows;
        } else {
          rowObj.ndtMethod = defaultNdtMethodForNewRows;
        }
        const w = rowObj.welderId;
        const hasWelder = Array.isArray(w)
          ? w.some((x) => x != null && String(x).trim() !== '')
          : w != null && String(w).trim() !== '';
        if (!hasWelder && defaultWelderIdsForNewWeldRows.length > 0) {
          rowObj.welderId = [...defaultWelderIdsForNewWeldRows];
        } else if (!Array.isArray(rowObj.welderId)) {
          rowObj.welderId = hasWelder ? [String(w).trim()] : [];
        }
        const ndtImported = String(rowObj.ndtMethod || '').trim().toUpperCase();
        const any = (...cells) => cells.some(spreadsheetCellProvided);
        if (ndtImported === 'MUT') {
          if (any(rowObj.mutAcc, rowObj.rtAcc)) rowObj.rtAccepted = spreadsheetTruthyPick(rowObj.mutAcc, rowObj.rtAcc);
          if (any(rowObj.mutCutOut, rowObj.rtCutOut)) rowObj.rtCutOut = spreadsheetTruthyPick(rowObj.mutCutOut, rowObj.rtCutOut);
        } else if (ndtImported === 'PAUT') {
          if (any(rowObj.pautAcc, rowObj.autAcc)) rowObj.autAccepted = spreadsheetTruthyPick(rowObj.pautAcc, rowObj.autAcc);
          if (any(rowObj.pautCutOut, rowObj.autCutOut)) rowObj.autCutOut = spreadsheetTruthyPick(rowObj.pautCutOut, rowObj.autCutOut);
        } else if (ndtImported === 'AUT') {
          if (any(rowObj.autAcc)) rowObj.autAccepted = spreadsheetTruthyPick(rowObj.autAcc);
          if (any(rowObj.autCutOut)) rowObj.autCutOut = spreadsheetTruthyPick(rowObj.autCutOut);
        } else {
          if (any(rowObj.rtAcc)) rowObj.rtAccepted = spreadsheetTruthyPick(rowObj.rtAcc);
          if (any(rowObj.rtCutOut)) rowObj.rtCutOut = spreadsheetTruthyPick(rowObj.rtCutOut);
        }
        if (any(rowObj.repair)) rowObj.repair = spreadsheetTruthyPick(rowObj.repair);
        return rowObj;
      });

      setWeldLogRows((prevRows) => [...prevRows, ...mappedRows]);
      setWeldEntryRows((prevRows) => [...prevRows, ...mappedRows]);
      message.success('Excel data imported successfully.');
      handleUploadModalCancel();
    } catch (error) {
      console.error('Excel upload error:', error);
      message.error('Failed to read the Excel file. Please try again.');
    }
  };

  // Fetch pipe tally data
  const fetchPipeTallyData = useCallback(async () => {
    if (!dprLogId) return;
    setIsLoadingPipeTally(true);
    try {
      const cacheKey = getCacheKey(dprLogId, shift);
      const response = await getOrFetch(__dprPipeTallyCache, cacheKey, () =>
        axiosConfig.get(`/dpr-pipe-tally?dprLogId=${dprLogId}&shift=${shift}`)
      );

      console.log('Pipe Tally API Response:', response);
      console.log('Response data:', response.data);

      let testTypesData = null;

      if (response.data && response.data.data) {
        testTypesData = response.data.data.testTypes;
      }

      // Store all testTypes
      if (testTypesData && Array.isArray(testTypesData) && testTypesData.length > 0) {
        setTestTypes(testTypesData);

        // Also update rtProgress for backward compatibility (find RT test type)
        const rtTestType = testTypesData.find((test) => test.testType === 'RT');
        if (rtTestType) {
          setRtProgress({
            firstRtWeld: rtTestType.firstWeldNo || '',
            lastRtWeld: rtTestType.lastWeldNo || '',
            totalRtAcceptedWelds: rtTestType.totalCompleted || 0
          });
        }
      } else {
        setTestTypes([]);
      }
    } catch (error) {
      console.error('Error fetching pipe tally data:', error);
    } finally {
      setIsLoadingPipeTally(false);
    }
  }, [shift, dprLogId]);

  // Fetch productivity/weld log data
  const fetchProductivityData = useCallback(async () => {
    if (!dprLogId) return;
    setIsLoadingWeldLog(true);
    try {
      const cacheKey = getCacheKey(dprLogId, shift);
      const response = await getOrFetch(__dprProductivityCache, cacheKey, () =>
        axiosConfig.get(`/dpr-productivity?dprLogId=${dprLogId}&shift=${shift}`)
      );

      let weldLogMetricsData = null;
      let notesData = null;
      let welderConfigsData = null;
      let welderPersonnelData = null;
      let defectTypesData = null;

      if (response.data && response.data.data) {
        // Backward compatible: API may return either top-level productivity or `mainlineProductivity`.
        const payload = response.data.data;
        const mainlines = Array.isArray(payload?.mainlineProductivity) ? payload.mainlineProductivity : [];
        const bySelected =
          mainlines.find((m) => {
            const mid = m?.mainlineId ?? m?.productionJointId ?? m?.sowId;
            const sel = selectedPipeline?.id ?? selectedPipeline?.productionJointId ?? sowId;
            return sel != null && mid != null && String(mid) === String(sel);
          }) || null;
        const base =
          payload?.weldLogMetrics ? payload :
          (bySelected || (mainlines.length > 0 ? mainlines[0] : payload));
        weldLogMetricsData = base?.weldLogMetrics;
        notesData = base?.notes;
        welderConfigsData = base?.welderConfigs ?? payload?.welderConfigs;
        welderPersonnelData = base?.welderPersonnel ?? payload?.welderPersonnel;
        defectTypesData = base?.defectTypes ?? payload?.defectTypes;
        if (response.data.data.reportNo) {
          setReportNo(response.data.data.reportNo);
        }
      }

      // Store welder configs from the response
      if (welderConfigsData && Array.isArray(welderConfigsData)) {
        setWelderConfigs(welderConfigsData);
      } else {
        setWelderConfigs([]);
      }

      // Welder dropdown: project-level list from productivity + same-day DPR personnel welders (often not on productivity payload)
      let personnelEntriesForWelders = [];
      try {
        const personnelRes = await getOrFetch(__dprPersonnelCache, cacheKey, () =>
          axiosConfig.get(`/dpr-personnel?dprLogId=${dprLogId}&shift=${shift}`)
        );
        personnelEntriesForWelders = extractPersonnelEntriesFromDprResponse(personnelRes);
      } catch (personnelErr) {
        console.warn(
          '[ProductivityLog] /dpr-personnel fetch failed; weld log uses productivity welder list only:',
          personnelErr?.message || personnelErr
        );
      }
      setWelderPersonnel(
        mergeWelderPersonnelFromProductivityAndDpr(welderPersonnelData, personnelEntriesForWelders)
      );

      // Store defect types from the response
      if (defectTypesData && Array.isArray(defectTypesData)) {
        setDefectTypes(defectTypesData);
      } else {
        setDefectTypes([]);
      }

      // NEW API: dailyWeldLog array (already flattened, includes ndtType + status labels)
      if (Array.isArray(response?.data?.data?.mainlineProductivity) || Array.isArray(response?.data?.data?.dprProductivity?.mainlineProductivity)) {
        const payload = response.data.data;
        const mainlines = Array.isArray(payload?.mainlineProductivity)
          ? payload.mainlineProductivity
          : (Array.isArray(payload?.dprProductivity?.mainlineProductivity) ? payload.dprProductivity.mainlineProductivity : []);
        const base =
          mainlines.find((m) => {
            const mid = m?.mainlineId ?? m?.productionJointId ?? m?.sowId;
            const sel = selectedPipeline?.id ?? selectedPipeline?.productionJointId ?? sowId;
            return sel != null && mid != null && String(mid) === String(sel);
          }) || mainlines[0];
        if (base) {
          // Capture server-provided summary counts (preferred for the summary strip)
          setServerSummaryCounts({
            totalWelds: base.totalWelds ?? payload.totalWelds,
            acceptedCount: base.acceptedCount ?? payload.acceptedCount,
            rejectedCutout: base.rejectedCutout ?? payload.rejectedCutout,
            rejectedRepair: base.rejectedRepair ?? payload.rejectedRepair,
          });
        }

        if (base && Array.isArray(base.dailyWeldLog) && base.dailyWeldLog.length > 0) {
          let mapped = base.dailyWeldLog.map((e, idx) => {
            const ndtRaw = String(e.ndtType ?? e.ndtMethod ?? '').trim().toUpperCase();
            const ndtMethod = projectNdtMethods.includes(ndtRaw) ? ndtRaw : defaultNdtMethodForNewRows;
            const m = uiMetricsFromDailyWeldLogEntry(e, ndtMethod);
            const fromDefect = indicationsFromDefectInfo(e.defectInfo);
            const fromAlt = indicationsFromDefectInfo(e.indications);
            const indications =
              fromDefect.length > 0 ? fromDefect : (fromAlt.length > 0 ? fromAlt : []);
            return {
              id: `weld-daily-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 8)}`,
              date: e.date || base.date || '',
              jointNo: e.jointNo || '',
              ndtMethod,
              status: statusToInternal(e.status),
              welderId: Array.isArray(e.welderId) ? e.welderId : normalizeWelderIdsFromMetric(e.welderId),
              rtAccepted: m.rtAccepted,
              autAccepted: m.autAccepted,
              rtCutOut: m.rtCutOut,
              autCutOut: m.autCutOut,
              repair: e.repair === 1 || e.repair === '1' || e.repair === true,
              comments: e.comments ?? e.customerComments ?? '',
              crceComment: e.crceComment ?? e.crceComments ?? '',
              repairComment: e.repairComment ?? e.repairComments ?? '',
              ...(indications.length > 0 ? { indications } : {})
            };
          });
          const rootMetrics =
            Array.isArray(payload?.weldLogMetrics) && payload.weldLogMetrics.length > 0
              ? payload.weldLogMetrics
              : (Array.isArray(base?.weldLogMetrics) ? base.weldLogMetrics : weldLogMetricsData);
          mapped = mergeRichFieldsFromWeldLogMetrics(mapped, rootMetrics);
          setWeldLogRows(mapped);
          setWeldEntryRows(mapped);
          // Per-mainline notes on API; fall back to legacy root `payload.notes`
          const n = base?.notes ?? payload?.notes ?? '';
          setRemarks(n || '');
          return;
        }
        if (base) {
          setWeldLogRows([]);
          setWeldEntryRows([]);
          const n = base?.notes ?? payload?.notes ?? '';
          setRemarks(n || '');
          return;
        }
      }

      // Legacy API: weldLogMetrics array structure into rows
      if (weldLogMetricsData && Array.isArray(weldLogMetricsData) && weldLogMetricsData.length > 0) {
        const mapWeldLogMetricsToTableData = (weldLogMetrics) => {
          if (!Array.isArray(weldLogMetrics)) return [];
          return weldLogMetrics.map((metricsRow, rowIndex) => {
            const row = {};
            row.originalIndex = rowIndex;

            // Ensure unique ID for each row to fix selection issues
            const uniqueId = `weld-row-${rowIndex}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

            Object.defineProperty(row, 'id', {
              value: uniqueId,
              writable: true, // Make it writable for easier state management
              configurable: true,
              enumerable: true
            });

            (Array.isArray(metricsRow) ? metricsRow : []).forEach(metric => {
              if (metric.keyName && metric.keyName !== 'id') {
                const keyName = metric.keyName;
                let value = metric.value;

                // Handle special case for welderId (array field; API may send numbers, objects, or snake_case keys)
                if (keyName === 'welderId' || keyName === 'Welder ID' || keyName === 'Welder Id') {
                  row.welderId = normalizeWelderIdsFromMetric(value);
                }
                // Handle special case for defectInfo (nested array field)
                else if (keyName === 'defectInfo') {
                  row.indications = indicationsFromDefectInfo(value);
                }
                // Handle Comments field mappings
                else if (keyName === 'Customercomments' || keyName === 'comments') {
                  row.comments = value || '';
                } else if (keyName === 'crceComments' || keyName === 'crceComment') {
                  row.crceComment = value || '';
                } else if (keyName === 'repairComments' || keyName === 'repairComment') {
                  row.repairComment = value || '';
                }
                // Handle status field - normalize to lowercase; map indication/accept_indication to "Accept w/ Indication"
                else if (keyName === 'status') {
                  const statusVal = value ? String(value).toLowerCase() : '';
                  if (value === 'Accept' || value === 'accept' || statusVal === 'accept') {
                    row[keyName] = 'accept';
                  } else if (
                    value === 'AcceptWithIndication' || value === 'indication' ||
                    statusVal === 'indication' || statusVal === 'accept_indication' || statusVal === 'acceptindication'
                  ) {
                    row[keyName] = 'indication';
                  } else if (
                    statusVal === 'reject_repair' ||
                    statusVal === 'rejectedrepair' ||
                    statusVal === 'rejected(repair)' ||
                    value === 'RejectedRepair' ||
                    value === 'Rejected (Repair)'
                  ) {
                    row[keyName] = 'reject_repair';
                  } else if (
                    statusVal === 'reject_cutout' ||
                    statusVal === 'rejectedcutout' ||
                    statusVal === 'rejected(cutout)' ||
                    value === 'RejectedCutout' ||
                    value === 'Rejected (Cutout)'
                  ) {
                    row[keyName] = 'reject_cutout';
                  } else if (value === 'Reject' || value === 'reject' || statusVal === 'reject') {
                    row[keyName] = 'reject';
                  } else {
                    row[keyName] = statusVal || 'accept';
                  }
                }
                // Handle RT / AUT inspection field mappings (API uses separate keyNames per NDT)
                else if (keyName === 'rtAcc' || keyName === 'rtAccepted') {
                  if (value !== null && value !== undefined && value !== '') {
                    row.rtAccepted = (value === '1' || value === 1 || value === true || value === 'true');
                  }
                } else if (keyName === 'autAcc' || keyName === 'autAccepted') {
                  if (value !== null && value !== undefined && value !== '') {
                    row.autAccepted = (value === '1' || value === 1 || value === true || value === 'true');
                  }
                } else if (keyName === 'rtRepairAccepted' || keyName === 'repairAccepted') {
                  if (value !== null && value !== undefined && value !== '') {
                    row.repairAccepted = (value === '1' || value === 1 || value === true || value === 'true');
                  }
                } else if (keyName === 'autRepairAccepted') {
                  if (value !== null && value !== undefined && value !== '') {
                    row.autRepairAccepted = (value === '1' || value === 1 || value === true || value === 'true');
                  }
                } else if (keyName === 'blowThru' || keyName === 'blowThrough') {
                  row.blowThrough = (value === '1' || value === 1 || value === true || value === 'true');
                } else if (keyName === 'stn1CutOut') {
                  row.stn1CutOut = (value === '1' || value === 1 || value === true || value === 'true');
                } else if (keyName === 'ndtMethod' || keyName === 'NDT Method') {
                  const v = String(value || '').trim().toUpperCase();
                  row.ndtMethod = projectNdtMethods.includes(v) ? v : defaultNdtMethodForNewRows;
                } else if (keyName === 'additionalAut' || keyName === 'Additional_AUT' || keyName === 'Additional AUT') {
                  row.additionalAut = value != null && value !== undefined ? String(value) : '';
                } else if (keyName === 'rtCutOut') {
                  if (value !== null && value !== undefined && value !== '') {
                    row.rtCutOut = (value === '1' || value === 1 || value === true || value === 'true');
                  }
                } else if (keyName === 'autCutOut') {
                  if (value !== null && value !== undefined && value !== '') {
                    row.autCutOut = (value === '1' || value === 1 || value === true || value === 'true');
                  }
                } else if (keyName === 'mutAcc' || keyName === 'mutAccepted') {
                  if (value !== null && value !== undefined && value !== '') {
                    row.rtAccepted = (value === '1' || value === 1 || value === true || value === 'true');
                  }
                } else if (keyName === 'mutCutOut') {
                  if (value !== null && value !== undefined && value !== '') {
                    row.rtCutOut = (value === '1' || value === 1 || value === true || value === 'true');
                  }
                } else if (keyName === 'pautAcc' || keyName === 'pautAccepted') {
                  if (value !== null && value !== undefined && value !== '') {
                    row.autAccepted = (value === '1' || value === 1 || value === true || value === 'true');
                  }
                } else if (keyName === 'pautCutOut') {
                  if (value !== null && value !== undefined && value !== '') {
                    row.autCutOut = (value === '1' || value === 1 || value === true || value === 'true');
                  }
                } else if (keyName === 'repair') {
                  row.repair = (value === '1' || value === 1 || value === true || value === 'true');
                } else if (keyName === 'rtRepairRej') {
                  row.rtRepairRej = value != null && value !== '' ? String(value) : '0';
                } else if (keyName === 'autRepairRej') {
                  row.autRepairRej = value != null && value !== '' ? String(value) : '0';
                } else if (keyName === 'subJoint') {
                  row.subJoint = value || [];
                } else if (keyName === 'jointNo' || keyName === 'Joint No') {
                  row.jointNo = value || '';
                }
                // Handle other fields normally
                else {
                  if (value === null || value === undefined || value === '') {
                    row[keyName] = metric.type === 'number' ? 0 : '';
                  } else {
                    if (metric.type === 'number') {
                      const numValue = parseFloat(value);
                      row[keyName] = isNaN(numValue) ? 0 : numValue;
                    } else {
                      row[keyName] = String(value);
                    }
                  }
                }
              }
            });

            if (row.status === 'reject') {
              if (row.repair) {
                row.status = 'reject_repair';
              } else if (row.rtCutOut || row.autCutOut || row.stn1CutOut) {
                row.status = 'reject_cutout';
              }
            }

            return row;
          });
        };

        const mappedRows = mapWeldLogMetricsToTableData(weldLogMetricsData);
        setWeldLogRows(mappedRows);
        setWeldEntryRows(mappedRows); // Also update entry rows to stay in sync
        setOriginalWeldLogMetrics(weldLogMetricsData);
        setDeletedRowIndices([]);
      } else {
        setWeldLogRows([]);
        setWeldEntryRows([]);
      }

      // Set notes/remarks
      if (notesData) {
        setRemarks(notesData);
      } else {
        setRemarks('');
      }
    } catch (error) {
      console.error('Error fetching productivity data:', error);
      setWeldLogRows([]);
      setRemarks('');
    } finally {
      setIsLoadingWeldLog(false);
    }
  }, [shift, dprLogId]);

  // Sync initial productivity data if provided as props
  useEffect(() => {
    const data = initialProductivityData && typeof initialProductivityData === 'object' ? initialProductivityData : {};
    if (data.rtProgress && typeof data.rtProgress === 'object') {
      setRtProgress(data.rtProgress);
    }
    if (Array.isArray(data.weldLogRows) && data.weldLogRows.length > 0) {
      setWeldLogRows(data.weldLogRows);
      setWeldEntryRows(data.weldLogRows);
    }
    if (data.remarks != null && data.remarks !== '') {
      setRemarks(data.remarks);
    }
  }, [initialProductivityData]);

  // Refetch data on shift change
  useEffect(() => {
    if (dprLogId) {
      fetchPipeTallyData();
      fetchProductivityData();
    }
  }, [shift, dprLogId, fetchPipeTallyData, fetchProductivityData]);

  // Column name mapping for weldLogMetrics
  const columnNameMap = {
    'date': 'Date',
    'jointNo': 'Joint No',
    'rw': 'RW (Root Weld)',
    'stn1Welds': 'Stage 1 Welds',
    'blowThru': 'Blow Through',
    'stn1CutOut': 'Stage 1 Cut Out / Re-bevel',
    'rtAcc': 'RT Accepted',
    'rtReject': 'RT Rejected',
    'repair': 'Repair',
    'rtRepairRej': 'RT Repair Accepted',
    'rtCutOut': 'RT Cut Out',
    'additionalAut': 'Additional AUT',
    'type': 'Weld Type',
    'startLoc': 'Location',
    'endLoc': 'endLoc',
    'depth': 'Depth (mm)',
    'length': 'Length (mm)',
    'repairComment': 'Comments',
    'welderId': 'CRCE',
    'repairComment1': 'Description'
  };

  // Transform weldEntryRows to metrics format for API payload
  const transformWeldEntryRowsToMetrics = (rows) => {
    if (!Array.isArray(rows)) return [];
    return rows.map((row, rowIndex) => {
      // Helper to get value or default
      const getVal = (val, def = '') => (val !== undefined && val !== null ? String(val) : def);
      const getBoolVal = (val) => (val ? '1' : '0');

      // Status for API (weld log metrics enum)
      const statusForApi =
        isRejectStatus(row.status)
          ? 'Reject'
          : row.status === 'indication'
            ? 'AcceptWithIndication'
            : 'Accept';

      const ndtMethodCodeRaw = String(row.ndtMethod || '').trim().toUpperCase();
      const ndtMethodCode = projectNdtMethods.includes(ndtMethodCodeRaw)
        ? ndtMethodCodeRaw
        : defaultNdtMethodForNewRows;
      const isAut = isAutFamily(ndtMethodCode);
      const rtSlotLabel = ndtMethodCode === 'MUT' ? 'MUT' : 'RT';
      const autSlotLabel = ndtMethodCode === 'PAUT' ? 'PAUT' : 'AUT';
      const rejectBit = isRejectStatus(row.status) ? '1' : '0';
      const repairBit = getBoolVal(row.repair);
      const accBit = getBoolVal(
        isAut
          ? (row.autAccepted !== undefined && row.autAccepted !== null ? row.autAccepted : row.rtAccepted)
          : row.rtAccepted
      );
      const cutBit = getBoolVal(
        isAut
          ? (row.autCutOut !== undefined && row.autCutOut !== null ? row.autCutOut : row.rtCutOut)
          : row.rtCutOut
      );
      const repairAccBit = getBoolVal(
        isAut
          ? (row.autRepairAccepted !== undefined && row.autRepairAccepted !== null
            ? row.autRepairAccepted
            : row.repairAccepted)
          : row.repairAccepted
      );
      const rtRepairRejPayload = isAut ? null : getVal(row.rtRepairRej, '0');
      const autRepairRejPayload = isAut ? getVal(row.autRepairRej ?? row.rtRepairRej, '0') : null;

      // RT-specific metrics: real values for RT welds; null when this row is AUT (API schema)
      const rtAccVal = isAut ? null : accBit;
      const rtRejectVal = isAut ? null : rejectBit;
      const rtRepairAccVal = isAut ? null : repairAccBit;
      const rtCutOutVal = isAut ? null : cutBit;

      // AUT-specific metrics: real values for AUT welds; null when this row is RT
      const autAccVal = isAut ? accBit : null;
      const autRejectVal = isAut ? rejectBit : null;
      const autRepairAccVal = isAut ? repairAccBit : null;
      const autCutOutVal = isAut ? cutBit : null;

      const metrics = [
        { id: '1', name: 'Date', keyName: 'date', value: getVal(row.date, new Date().toISOString().split('T')[0]), order: 1, editable: true, type: 'text', weldIndex: rowIndex, weldId: row.id || '' },
        { id: '2', name: 'Joint No', keyName: 'jointNo', value: getVal(row.jointNo), order: 2, editable: true, type: 'text', weldIndex: rowIndex, weldId: row.id || '' },
        { id: '3', name: 'RW (Repair Weld)', keyName: 'rw', value: row.repairWeld ? '1' : '0', order: 3, editable: true, type: 'text', weldIndex: rowIndex, weldId: row.id || '' },
        { id: '4', name: 'Station 1 Welds', keyName: 'stn1Welds', value: '1', order: 4, editable: true, type: 'number', weldIndex: rowIndex, weldId: row.id || '' },
        { id: '5', name: 'Blow Through', keyName: 'blowThru', value: getBoolVal(row.blowThrough), order: 5, editable: true, type: 'number', weldIndex: rowIndex, weldId: row.id || '' },
        { id: '6', name: 'Station 1 Cut Out ', keyName: 'stn1CutOut', value: getBoolVal(row.stn1CutOut), order: 6, editable: true, type: 'number', weldIndex: rowIndex, weldId: row.id || '' },
        { id: '7', name: `${rtSlotLabel} Accepted`, keyName: 'rtAcc', value: rtAccVal, order: 7, editable: true, type: 'number', weldIndex: rowIndex, weldId: row.id || '' },
        { id: '8', name: `${rtSlotLabel} Rejected`, keyName: 'rtReject', value: rtRejectVal, order: 8, editable: true, type: 'number', weldIndex: rowIndex, weldId: row.id || '' },
        { id: '9', name: 'Repair', keyName: 'repair', value: repairBit, order: 9, editable: true, type: 'number', weldIndex: rowIndex, weldId: row.id || '' },
        { id: '10', name: `${rtSlotLabel} Repair Accepted`, keyName: 'rtRepairAccepted', value: rtRepairAccVal, order: 10, editable: true, type: 'number', weldIndex: rowIndex, weldId: row.id || '' },
        { id: '11', name: `${rtSlotLabel} Cut Out`, keyName: 'rtCutOut', value: rtCutOutVal, order: 11, editable: true, type: 'number', weldIndex: rowIndex, weldId: row.id || '' },
        { id: '11a', name: `${autSlotLabel} Accepted`, keyName: 'autAcc', value: autAccVal, order: 12, editable: true, type: 'number', weldIndex: rowIndex, weldId: row.id || '' },
        { id: '11b', name: `${autSlotLabel} Rejected`, keyName: 'autReject', value: autRejectVal, order: 13, editable: true, type: 'number', weldIndex: rowIndex, weldId: row.id || '' },
        { id: '11c', name: `${autSlotLabel} Repair Accepted`, keyName: 'autRepairAccepted', value: autRepairAccVal, order: 14, editable: true, type: 'number', weldIndex: rowIndex, weldId: row.id || '' },
        { id: '11d', name: `${autSlotLabel} Cut Out`, keyName: 'autCutOut', value: autCutOutVal, order: 15, editable: true, type: 'number', weldIndex: rowIndex, weldId: row.id || '' },
        {
          id: '16a',
          name: 'Defect Info',
          keyName: 'defectInfo',
          order: 16.5,
          editable: true,
          type: 'array',
          isArray: true,
          weldIndex: rowIndex,
          weldId: row.id || `weld-${rowIndex}`,
          value: (row.indications && row.indications.length > 0) ? row.indications.map(ind => [
            { id: '12', name: 'Defect Type', keyName: 'defectType', order: 12, editable: true, type: 'enum', options: defectTypes || [], value: getVal(ind.defectType) },
            { id: '13', name: 'Start Location', keyName: 'startLoc', order: 13, editable: true, type: 'text', value: getVal(ind.start) },
            { id: '14', name: 'End Location', keyName: 'endLoc', order: 14, editable: true, type: 'text', value: getVal(ind.end) },
            { id: '15', name: 'Depth (mm)', keyName: 'depth', order: 15, editable: true, type: 'text', value: getVal(ind.depth) },
            { id: '16', name: 'Length (mm)', keyName: 'length', order: 16, editable: true, type: 'text', value: getVal(ind.length) },
            { id: '17', name: 'Defect Accepted', keyName: 'defectAccepted', order: 17, editable: true, type: 'text', value: ind.defectAccepted === true || ind.defectAccepted === 1 || ind.defectAccepted === '1' ? '1' : '0' },
            { id: '18', name: 'Height', keyName: 'height', order: 18, editable: true, type: 'text', value: getVal(ind.height) }
          ]) : []
        },
        { id: '17', name: 'Welder Id', keyName: 'welderId', value: Array.isArray(row.welderId) ? row.welderId : (row.welderId ? [row.welderId] : []), order: 17, editable: true, type: 'array', isArray: true, weldIndex: rowIndex, weldId: row.id || '' },
        { id: '18', name: 'Status', keyName: 'status', value: statusForApi, order: 18, editable: true, type: 'enum', options: ['Accept', 'AcceptWithIndication', 'Reject'], weldIndex: rowIndex, weldId: row.id || '' },
        { id: '19', name: 'Comments', keyName: 'comments', value: getVal(row.comments), order: 19, editable: true, type: 'text', weldIndex: rowIndex, weldId: row.id || '' },
        { id: '20', name: 'CRCE', keyName: 'crceComment', value: getVal(row.crceComment), order: 20, editable: true, type: 'text', weldIndex: rowIndex, weldId: row.id || '' },
        { id: '21', name: 'Repair Comment', keyName: 'repairComment', value: getVal(row.repairComment), order: 21, editable: true, type: 'text', weldIndex: rowIndex, weldId: row.id || '' },
        { name: 'SubJoint', keyName: 'subJoint', order: 999, editable: true, type: 'text', value: row.subJoint || [], weldIndex: rowIndex, weldId: row.id || '' },
        { name: 'NDT Method', keyName: 'ndtMethod', order: 999, editable: true, type: 'text', value: ndtMethodCode, weldIndex: rowIndex, weldId: row.id || '' },
        { name: 'Additional AUT', keyName: 'additionalAut', order: 999, editable: true, type: 'text', value: getVal(row.additionalAut, ''), weldIndex: rowIndex, weldId: row.id || '' },
        { name: 'RT Repair Rejected', keyName: 'rtRepairRej', order: 999, editable: true, type: 'text', value: rtRepairRejPayload, weldIndex: rowIndex, weldId: row.id || '' },
        { name: 'AUT Repair Rejected', keyName: 'autRepairRej', order: 999, editable: true, type: 'text', value: autRepairRejPayload, weldIndex: rowIndex, weldId: row.id || '' }
      ];

      return metrics;
    });
  };

  // Parse joint ID: any prefix + trailing digits (e.g. J001 -> { prefix: 'J', numStr: '001', num: 1 }; weld01 -> { prefix: 'weld', numStr: '01', num: 1 })
  const parseJoint = (str) => {
    if (str == null || typeof str !== 'string') return null;
    const trimmed = str.trim();
    // Use non-greedy (.*?) so prefix is only the part before trailing digits (e.g. "j10" -> prefix "j", numStr "10" not "j1" / "0")
    const m = trimmed.match(/^(.*?)(\d+)$/);
    if (!m) return null;
    return { prefix: m[1], numStr: m[2], num: parseInt(m[2], 10) };
  };

  // Joint Range Validation – supports any prefix/word before numbers (e.g. J001, weld01, joint-05)
  const validateJointRange = (firstJoint, lastJoint) => {
    if (!firstJoint || !String(firstJoint).trim()) {
      return 'First Joint is required';
    }

    const first = parseJoint(firstJoint);
    if (!first) {
      return 'First Joint must end with numbers (e.g., J001, weld01, joint-05)';
    }

    if (lastJoint && String(lastJoint).trim()) {
      const last = parseJoint(lastJoint);
      if (!last) {
        return 'Last Joint must end with numbers (e.g., J010, weld03, joint-05)';
      }
      if (last.prefix.toLowerCase() !== first.prefix.toLowerCase()) {
        return 'First Joint and Last Joint must have the same prefix (e.g., weld01 to weld05, not weld01 to joint-05)';
      }
      if (last.num < first.num) {
        return 'Last Joint number must be greater than or equal to First Joint';
      }
    }

    return '';
  };

  // Generate Joint Rows – any prefix + number range; creates exact count, no duplicates in output
  const generateJointRows = (firstJoint, lastJoint, opts = {}) => {
    const raw = String(opts.ndtMethod || '').trim().toUpperCase();
    const ndtMethod = projectNdtMethods.includes(raw) ? raw : defaultNdtMethodForNewRows;
    const welderId =
      Array.isArray(opts.defaultWelderIds) && opts.defaultWelderIds.length > 0
        ? [...opts.defaultWelderIds]
        : [];

    const first = parseJoint(firstJoint);
    if (!first) return [];

    const padding = Math.max(first.numStr.length, lastJoint ? parseJoint(lastJoint)?.numStr?.length || 0 : 0);

    if (!lastJoint || !String(lastJoint).trim()) {
      return [{
        id: `joint-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        jointNo: firstJoint.trim(),
        status: 'accept',
        welderId,
        selected: false,
        ndtMethod
      }];
    }

    const last = parseJoint(lastJoint);
    if (!last || last.num < first.num) return [];

    const prefix = first.prefix;
    const rows = [];
    for (let i = first.num; i <= last.num; i++) {
      const numStr = String(i).padStart(padding, '0');
      const jointNo = prefix + numStr;
      rows.push({
        id: `joint-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 8)}`,
        jointNo,
        status: 'accept',
        welderId: [...welderId],
        selected: false,
        ndtMethod
      });
    }
    return rows;
  };

  // Delete Row Handlers
  const handleDeleteRow = (id) => {
    setRowToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setRowToDelete(null);
    setIsBulkDelete(false);
  };

  const handleDeleteSelected = () => {
    setIsBulkDelete(true);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!rowToDelete && !isBulkDelete) return;

    try {
      // Remove the row from state
      // Remove the row from state
      let updatedRows = [];
      if (isBulkDelete) {
        updatedRows = weldEntryRows.filter(row => !row.selected);
      } else {
        updatedRows = weldEntryRows.filter(row => String(row.id) !== String(rowToDelete));
      }
      setWeldEntryRows(updatedRows);
      setIsDeleteModalOpen(false);
      setRowToDelete(null);
      setIsBulkDelete(false);

      // Immediately call PUT API with updated data (excluding deleted row)
      if (dprLogId && updatedRows.length >= 0) {
        setIsSaving(true);
        try {
          // Transform updated rows to API format
          const weldLogMetrics = transformWeldEntryRowsToMetrics(mapRowsForProductivityApi(updatedRows));
          const dailyWeldLog = (Array.isArray(updatedRows) ? updatedRows : []).map((r) =>
            buildDailyWeldLogPayloadRow(r, defaultNdtMethodForNewRows)
          ).filter((e) => e.jointNo);
          const testTypesList = Array.isArray(testTypes) ? [...testTypes] : [];
          const rtFromApi = testTypesList.find((t) => String(t.testType || '').toUpperCase() === 'RT');
          const dprProductivityBody = {
            dprLogId: dprLogId,
            reportNo: reportNo || `DPR-${dprLogId}-P-v1`,
            weldLogMetrics: weldLogMetrics,
            mainlineProductivity: [
              {
                mainlineLabel: selectedPipeline?.pipeGroup ? `Mainline: ${selectedPipeline.pipeGroup}` : (selectedPipeline?.pipeGroupName ? `Mainline: ${selectedPipeline.pipeGroupName}` : ''),
                mainlineId: selectedPipeline?.id ?? selectedPipeline?.productionJointId ?? null,
                sowId: sowId ?? selectedPipeline?.sowId ?? null,
                productionJointId: selectedPipeline?.productionJointId ?? null,
                notes: remarks || '',
                dailyWeldLog,
                totalWelds: summaryAsNumber(summaryFieldInputs.totalWelds),
                acceptedCount: summaryAsNumber(summaryFieldInputs.acceptedCounts),
                rejectedCutout: summaryAsNumber(summaryFieldInputs.rejectedCutout),
                rejectedRepair: summaryAsNumber(summaryFieldInputs.rejectedRepair),
              }
            ],
            firstRtWeld: rtFromApi?.firstWeldNo ?? rtProgress.firstRtWeld ?? '',
            lastRtWeld: rtFromApi?.lastWeldNo ?? rtProgress.lastRtWeld ?? '',
            totalRtAcceptedWelds: Number(
              rtFromApi?.totalCompleted ?? rtProgress.totalRtAcceptedWelds ?? 0
            )
          };
          if (testTypesList.length > 0) {
            dprProductivityBody.testTypes = testTypesList;
          }

          const payload = {
            shift: shift,
            dprProductivity: dprProductivityBody
          };

          await axiosConfig.put('/dpr-productivity', payload);
          message.success('Weld entry deleted successfully');

          // Invalidate caches so shared panels refresh from server once.
          const cacheKey = getCacheKey(dprLogId, shift);
          __dprProductivityCache.delete(cacheKey);
          __dprPipeTallyCache.delete(cacheKey);
          __dprPersonnelCache.delete(cacheKey);

          // Refetch data to update UI
          await fetchProductivityData();
          await fetchPipeTallyData();
        } catch (error) {
          console.error('Error deleting weld entry:', error);
          message.error('Failed to delete weld entry');
          // Revert state on error
          setWeldEntryRows(weldEntryRows);
        } finally {
          setIsSaving(false);
        }
      }
    } catch (error) {
      console.error('Error in delete confirmation:', error);
    }
  };

  const handleSave = async () => {
    if (isShiftSkipped) return false;
    if (!dprLogId) {
      message.error('DPR Log ID is required to save');
      return false;
    }

    // Only enforce "no duplicate joint across RT & AUT" when both RT and AUT are enabled
    if (projectNdtMethods.includes('RT') && projectNdtMethods.includes('AUT')) {
      const rtJoints = new Set();
      const autJoints = new Set();
      weldEntryRows.forEach((row) => {
        const j = String(row.jointNo || '').trim().toLowerCase();
        if (!j) return;
        if (row.ndtMethod === 'AUT') autJoints.add(j);
        else rtJoints.add(j);
      });
      const overlap = [...rtJoints].filter((j) => autJoints.has(j));
      if (overlap.length > 0) {
        message.error(
          `The same joint cannot be listed for both RT and AUT. Check: ${overlap.slice(0, 5).join(', ')}${overlap.length > 5 ? '…' : ''}`
        );
        return false;
      }
    }

    const invalidRows = weldEntryRows.filter((row) => {
      const jointMissing = !String(row.jointNo || '').trim();
      const statusMissing = !row.status;
      return jointMissing || statusMissing;
    });
    if (invalidRows.length > 0) {
      message.error(
        'Please fill in mandatory fields (Joint No, Weld Status) for all rows.'
      );
      return false;
    }


    // RT status validations removed - no longer enforcing specific RT field values based on status

    setIsSaving(true);
    try {
      const testTypesList = Array.isArray(testTypes) ? [...testTypes] : [];
      const rtFromApi = testTypesList.find((t) => String(t.testType || '').toUpperCase() === 'RT');
      const firstRtWeld = rtFromApi?.firstWeldNo ?? rtProgress.firstRtWeld ?? '';
      const lastRtWeld = rtFromApi?.lastWeldNo ?? rtProgress.lastRtWeld ?? '';
      const totalRtAcceptedWelds = Number(
        rtFromApi?.totalCompleted ?? rtProgress.totalRtAcceptedWelds ?? 0
      );

      // Use transformWeldEntryRowsToMetrics instead of the old function
      // and pass weldEntryRows (the edited state)
      const weldLogMetrics = transformWeldEntryRowsToMetrics(mapRowsForProductivityApi(weldEntryRows));

      // NEW API payload: mainlineProductivity[].dailyWeldLog
      const dailyWeldLog = (Array.isArray(weldEntryRows) ? weldEntryRows : []).map((r) =>
        buildDailyWeldLogPayloadRow(r, defaultNdtMethodForNewRows)
      ).filter((e) => e.jointNo);

      const dprProductivityBody = {
        dprLogId: dprLogId,
        firstRtWeld: firstRtWeld,
        lastRtWeld: lastRtWeld,
        totalRtAcceptedWelds,
        weldLogMetrics: weldLogMetrics,
        mainlineProductivity: [
          {
            mainlineLabel: selectedPipeline?.pipeGroup ? `Mainline: ${selectedPipeline.pipeGroup}` : (selectedPipeline?.pipeGroupName ? `Mainline: ${selectedPipeline.pipeGroupName}` : ''),
            mainlineId: selectedPipeline?.id ?? selectedPipeline?.productionJointId ?? null,
            sowId: sowId ?? selectedPipeline?.sowId ?? null,
            productionJointId: selectedPipeline?.productionJointId ?? null,
            notes: remarks || '',
            dailyWeldLog,
            totalWelds: summaryAsNumber(summaryFieldInputs.totalWelds),
            acceptedCount: summaryAsNumber(summaryFieldInputs.acceptedCounts),
            rejectedCutout: summaryAsNumber(summaryFieldInputs.rejectedCutout),
            rejectedRepair: summaryAsNumber(summaryFieldInputs.rejectedRepair),
          }
        ],
      };
      if (testTypesList.length > 0) {
        dprProductivityBody.testTypes = testTypesList;
      }

      const payload = {
        shift: shift,
        dprProductivity: dprProductivityBody
      };

      console.log("Saving Payload:", JSON.stringify(payload, null, 2));

      await axiosConfig.put('/dpr-productivity', payload);

      message.success('Productivity report saved successfully');

      // Invalidate caches so shared panels refresh from server once.
      const cacheKey = getCacheKey(dprLogId, shift);
      __dprProductivityCache.delete(cacheKey);
      __dprPipeTallyCache.delete(cacheKey);
      __dprPersonnelCache.delete(cacheKey);

      await fetchProductivityData();
      await fetchPipeTallyData();

      // IMPORTANT: do not auto-navigate to next section for now.
      return true;
    } catch (error) {
      console.error('Error saving productivity report:', error);
      message.error('Failed to save productivity report');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddProductivity = () => {
    setIsAddModalVisible(true);
    setNewProductivity({
      firstJoint: '',
      lastJoint: '',
      ndtMethod: defaultNdtMethodForNewRows
    });
    setValidationError('');
  };

  const handleAddModalCancel = () => {
    setIsAddModalVisible(false);
    setNewProductivity({
      firstJoint: '',
      lastJoint: '',
      ndtMethod: defaultNdtMethodForNewRows
    });
    setValidationError('');
  };

  const handleAddRecords = () => {
    const error = validateJointRange(newProductivity.firstJoint, newProductivity.lastJoint);

    if (error) {
      setValidationError(error);
      return;
    }

    const generatedRows = generateJointRows(newProductivity.firstJoint, newProductivity.lastJoint, {
      ndtMethod: newProductivity.ndtMethod,
      defaultWelderIds: defaultWelderIdsForNewWeldRows
    });

    // Filter duplicates (case-insensitive so weld01 and Weld01 are treated as same)
    const existingJoints = new Set(weldEntryRows.map(row => (row.jointNo || '').trim().toLowerCase()));
    const newRows = generatedRows.filter(row => !existingJoints.has((row.jointNo || '').trim().toLowerCase()));

    if (newRows.length === 0) {
      message.warning('All joint numbers in this range already exist.');
      handleAddModalCancel(); // Close modal anyway? Or keep open? Usually close to reset.
      return;
    }

    setWeldEntryRows((prev) => [...prev, ...newRows]);
    handleAddModalCancel();

    if (newRows.length < generatedRows.length) {
      message.success(`Added ${newRows.length} new records (${generatedRows.length - newRows.length} duplicates skipped)`);
    } else {
      message.success(`${newRows.length} weld record(s) added successfully`);
    }
  };

  const handleDiscardChanges = () => {
    setWeldEntryRows([]);
    message.info('Changes discarded');
  };

  const handleSelectAllByMethod = (methodCode, checked) => {
    const target = String(methodCode || '').trim().toUpperCase();
    setWeldEntryRows((rows) =>
      (Array.isArray(rows) ? rows : []).map((row) => {
        const m = String(row.ndtMethod || '').trim().toUpperCase();
        return m === target ? { ...row, selected: checked } : row;
      })
    );
  };

  const handleSelectRow = (id, checked) => {
    setWeldEntryRows((rows) => {
      const list = Array.isArray(rows) ? rows : [];
      return list.map((row) => (row.id === id ? { ...row, selected: checked } : row));
    });
  };

  const handleStatusChange = (id, status) => {
    setWeldEntryRows((rows) =>
      (Array.isArray(rows) ? rows : []).map((row) =>
        row.id === id ? weldRowStatusSideEffects(row, status) : row
      )
    );
  };

  const handleWelderIdChange = (id, welderId) => {
    const normalized = normalizeWelderIdsFromMetric(
      Array.isArray(welderId) ? welderId : (welderId != null && welderId !== '' ? [welderId] : [])
    );
    setWeldEntryRows((rows) =>
      (Array.isArray(rows) ? rows : []).map((row) => (row.id === id ? { ...row, welderId: normalized } : row))
    );
  };

  const handleBulkStatusChange = (status) => {
    setWeldEntryRows((rows) =>
      (Array.isArray(rows) ? rows : []).map((row) =>
        row.selected ? weldRowStatusSideEffects(row, status) : row
      )
    );
    // Keep selection so user can still set welder from header dropdown; header reverts only after welder is selected
  };

  const handleBulkWelderIdChange = (welderId) => {
    const value = normalizeWelderIdsFromMetric(
      Array.isArray(welderId) ? welderId : (welderId != null ? [welderId] : [])
    );
    setWeldEntryRows((rows) =>
      (Array.isArray(rows) ? rows : []).map((row) =>
        row.selected ? { ...row, welderId: value, selected: false } : row
      )
    );
    // Clear selection after welder is applied so header goes back to normal (STATUS / WELDER ID)
  };

  const handleEditRow = (row) => {
    setEditingRow(row);
  };

  const handleUpdateRow = (updatedRow) => {
    console.log("Updating row:", updatedRow);
    setWeldEntryRows((rows) => {
      const updated = (Array.isArray(rows) ? rows : []).map((row) =>
        String(row.id) === String(updatedRow.id) ? { ...updatedRow } : row
      );
      console.log("New rows after update:", updated);
      return updated;
    });
    setEditingRow(null);
    message.success('Weld entry updated successfully');
  };

  const uploadProps = {
    accept: '.xlsx,.xls',
    multiple: false,
    fileList: uploadFileList,
    beforeUpload: (file) => {
      const isExcelType =
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel';
      const isExcelExt = /\.(xlsx|xls)$/i.test(file.name || '');
      const isExcel = isExcelType || isExcelExt;

      if (!isExcel) {
        message.error('Please upload a valid Excel file (.xls or .xlsx).');
        return Upload.LIST_IGNORE;
      }

      setUploadFileList([file]);
      return false;
    },
    onRemove: () => {
      setUploadFileList([]);
    }
  };

  const normalizedProjectNdtMethod = useMemo(() => {
    const raw =
      selectedProject?.ndtTypes ??
      selectedProject?.ndt_types ??
      selectedProject?.ndtType ??
      selectedProject?.ndtMethodType ??
      selectedProject?.inspectionMethodType ??
      selectedProject?.inspectionMethod ??
      selectedProject?.ndtMethod ??
      selectedPipeline?.ndtTypes ??
      selectedPipeline?.ndt_types ??
      selectedPipeline?.ndtType ??
      selectedPipeline?.ndtMethod ??
      selectedPipeline?.inspectionMethod ??
      '';
    const joined = Array.isArray(raw) ? raw.filter(Boolean).join(',') : raw;
    const v = String(joined || '').trim().toUpperCase();
    if (v === 'AUT') return 'AUT';
    if (v === 'RT') return 'RT';
    if (v === 'BOTH') return 'BOTH';
    if (v === 'RT/AUT' || v === 'AUT/RT') return 'BOTH';
    if (v.includes('BOTH')) return 'BOTH';
    if (v.includes('RT') && v.includes('AUT')) return 'BOTH';
    return 'RT';
  }, [selectedProject, selectedPipeline]);

  const projectNdtMethods = useMemo(() => {
    const fromProject = projectNdtTypesArray(selectedProject);
    const fromPipeline = projectNdtTypesArray(selectedPipeline);
    const raw = (fromProject && fromProject.length ? fromProject : fromPipeline) || [];
    const cleaned = raw.map((x) => String(x || '').trim().toUpperCase()).filter(Boolean);
    // If not configured, default to RT + AUT (legacy behavior)
    if (cleaned.length === 0) return ['RT', 'AUT'];
    return [...new Set(cleaned)];
  }, [selectedProject, selectedPipeline]);

  const defaultNdtMethodForNewRows = projectNdtMethods[0] || 'RT';

  const mapRowsForProductivityApi = useCallback((rows) => {
    if (!Array.isArray(rows)) return [];
    const withComments = (r) => ({
      ...r,
      comments: r.comments != null ? String(r.comments) : '',
      crceComment: r.crceComment != null ? String(r.crceComment) : ''
    });
    if (projectNdtMethods.length === 1) {
      const one = projectNdtMethods[0];
      return rows.map((r) => ({ ...withComments(r), ndtMethod: one }));
    }
    return rows.map((r) => {
      const v = String(r.ndtMethod || '').trim().toUpperCase();
      const ndt = projectNdtMethods.includes(v) ? v : defaultNdtMethodForNewRows;
      return { ...withComments(r), ndtMethod: ndt };
    });
  }, [projectNdtMethods, defaultNdtMethodForNewRows]);

  /** NDT progress rows, rendered based on project inspection methods. */
  const progressTestTypesDisplay = useMemo(() => {
    const fromApi = Array.isArray(testTypes) ? testTypes : [];
    const byCode = new Map();
    fromApi.forEach((t) => {
      const code = String(t.testType || '').toUpperCase();
      if (code) byCode.set(code, t);
    });
    const primaryOrder = projectNdtMethods.map((code) => ({
      code,
      defaults: {
        testType: code,
        testLabel: `${code} inspection`,
        firstWeldNo: '',
        lastWeldNo: '',
        totalCompleted: 0,
      },
    }));
    const ordered = primaryOrder.map(({ code, defaults }) => {
      const existing = byCode.get(code);
      byCode.delete(code);
      return existing ? { ...defaults, ...existing, testType: existing.testType || code } : defaults;
    });
    return ordered;
  }, [testTypes, projectNdtMethods]);

  const safeWeldEntryRows = Array.isArray(weldEntryRows) ? weldEntryRows : [];
  const weldRowCountsByMethod = useMemo(() => {
    const m = new Map();
    safeWeldEntryRows.forEach((r) => {
      const code = String(r.ndtMethod || '').trim().toUpperCase() || defaultNdtMethodForNewRows;
      m.set(code, (m.get(code) || 0) + 1);
    });
    return m;
  }, [safeWeldEntryRows, defaultNdtMethodForNewRows]);
  const weldLogRowsByMethod = useMemo(() => {
    const m = new Map();
    projectNdtMethods.forEach((code) => m.set(code, []));
    safeWeldEntryRows.forEach((r) => {
      const raw = String(r.ndtMethod || '').trim().toUpperCase();
      const code = projectNdtMethods.includes(raw) ? raw : defaultNdtMethodForNewRows;
      m.set(code, [...(m.get(code) || []), r]);
    });
    return m;
  }, [safeWeldEntryRows, projectNdtMethods, defaultNdtMethodForNewRows]);

  const methodsWithWeldRows = useMemo(
    () => projectNdtMethods.filter((code) => (weldLogRowsByMethod.get(code) || []).length > 0),
    [projectNdtMethods, weldLogRowsByMethod]
  );

  /** Hide progress dashboards when there are no weld-log rows for that method. */
  const visibleProgressTestTypes = useMemo(
    () =>
      progressTestTypesDisplay.filter((testType) => {
        const code = String(testType.testType || '').toUpperCase();
        return (weldRowCountsByMethod.get(code) || 0) > 0;
      }),
    [progressTestTypesDisplay, weldRowCountsByMethod]
  );

  /** Per-mainline weld counts for summary strip (above weld log rows). */
  const weldSummaryStats = useMemo(() => {
    const rows = safeWeldEntryRows;
    const total = rows.length;
    const acceptedCounts = rows.filter((r) => r.status === 'accept' || r.status === 'indication').length;
    const rejectedCutout = rows.filter(
      (r) =>
        r.status === 'reject_cutout' ||
        (isRejectStatus(r.status) && Boolean(r.rtCutOut || r.autCutOut || r.stn1CutOut))
    ).length;
    const rejectedRepair = rows.filter(
      (r) =>
        r.status === 'reject_repair' ||
        (isRejectStatus(r.status) && Boolean(r.repair))
    ).length;
    return { total, acceptedCounts, rejectedCutout, rejectedRepair };
  }, [safeWeldEntryRows]);

  /** Seed inputs when weld data finishes loading or log/shift changes — avoids overwriting on every row keystroke */
  useEffect(() => {
    if (isLoadingWeldLog) return;
    const srv = serverSummaryCounts && typeof serverSummaryCounts === 'object' ? serverSummaryCounts : null;
    const srvTotal = srv?.totalWelds;
    const srvAccepted = srv?.acceptedCount;
    const srvCutout = srv?.rejectedCutout;
    const srvRepair = srv?.rejectedRepair;
    setSummaryFieldInputs({
      totalWelds: String(srvTotal ?? weldSummaryStats.total),
      acceptedCounts: String(srvAccepted ?? weldSummaryStats.acceptedCounts),
      rejectedCutout: String(srvCutout ?? weldSummaryStats.rejectedCutout),
      rejectedRepair: String(srvRepair ?? weldSummaryStats.rejectedRepair)
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally omit weldSummaryStats so edits aren't wiped when rows change locally
  }, [isLoadingWeldLog, dprLogId, shift, serverSummaryCounts]);

  // Columns for template download/processing
  const columns = [
    { title: '#', key: 'index', dataIndex: 'index' },
    { title: 'Joint No', dataIndex: 'jointNo', key: 'jointNo' },
    { title: 'Rw', dataIndex: 'rw', key: 'rw' },
    { title: 'STN1 Welds', dataIndex: 'stn1Welds', key: 'stn1Welds' },
    { title: 'BLOW THRU', dataIndex: 'blowThru', key: 'blowThru' },
    { title: 'STN 1 Cut Out', dataIndex: 'stn1CutOut', key: 'stn1CutOut' },
    { title: 'NDT Method', dataIndex: 'ndtMethod', key: 'ndtMethod' },
    { title: 'RT ACC', dataIndex: 'rtAcc', key: 'rtAcc' },
    { title: 'RT Reject', dataIndex: 'rtReject', key: 'rtReject' },
    { title: 'REPAIR', dataIndex: 'repair', key: 'repair' },
    { title: 'RT Repair REJ', dataIndex: 'rtRepairRej', key: 'rtRepairRej' },
    { title: 'RT Cut Out', dataIndex: 'rtCutOut', key: 'rtCutOut' },
    { title: 'AUT ACC', dataIndex: 'autAcc', key: 'autAcc' },
    { title: 'AUT Reject', dataIndex: 'autReject', key: 'autReject' },
    { title: 'AUT Repair REJ', dataIndex: 'autRepairRej', key: 'autRepairRej' },
    { title: 'AUT Cut Out', dataIndex: 'autCutOut', key: 'autCutOut' },
    { title: 'MUT ACC', dataIndex: 'mutAcc', key: 'mutAcc' },
    { title: 'MUT Reject', dataIndex: 'mutReject', key: 'mutReject' },
    { title: 'MUT Cut Out', dataIndex: 'mutCutOut', key: 'mutCutOut' },
    { title: 'PAUT ACC', dataIndex: 'pautAcc', key: 'pautAcc' },
    { title: 'PAUT Reject', dataIndex: 'pautReject', key: 'pautReject' },
    { title: 'PAUT Cut Out', dataIndex: 'pautCutOut', key: 'pautCutOut' },
    { title: 'Type', dataIndex: 'type', key: 'type' },
    { title: 'Start Loc', dataIndex: 'startLoc', key: 'startLoc' },
    { title: 'End Loc', dataIndex: 'endLoc', key: 'endLoc' },
    { title: 'Depth', dataIndex: 'depth', key: 'depth' },
    { title: 'Length', dataIndex: 'length', key: 'length' },
    { title: 'Welder ID', dataIndex: 'welderId', key: 'welderId' },
    { title: 'Repair Comment', dataIndex: 'repairComment', key: 'repairComment' },
    { title: 'Action', key: 'action' }
  ];

  panelExposeSnapshotRef.current = {
    weldEntryRows,
    remarks,
    summaryFieldInputs,
    defaultNdtMethodForNewRows,
    mainlineTitle,
    selectedPipeline,
    sowId,
    dprLogId,
    shift,
    fetchProductivityData,
    fetchPipeTallyData,
  };

  useImperativeHandle(ref, () => ({
    openAddWeld: () => setIsAddModalVisible(true),
    getWeldLogMetricsDraft: () => {
      const rows = Array.isArray(weldEntryRows) ? weldEntryRows : [];
      if (rows.length === 0) return [];
      return transformWeldEntryRowsToMetrics(mapRowsForProductivityApi(rows));
    },
    getMainlineDailyWeldLogDraft: () => {
      const snap = panelExposeSnapshotRef.current;
      const rows = Array.isArray(snap.weldEntryRows) ? snap.weldEntryRows : [];
      const defNdt = snap.defaultNdtMethodForNewRows || 'RT';
      const dailyWeldLog = rows.map((r) => buildDailyWeldLogPayloadRow(r, defNdt)).filter((e) => e.jointNo);
      const mt = snap.mainlineTitle;
      const sp = snap.selectedPipeline;
      const sid = snap.sowId;
      const s = snap.summaryFieldInputs || {};
      return {
        mainlineLabel: mt ? `Mainline: ${mt}` : (
          sp?.pipeGroup ? `Mainline: ${sp.pipeGroup}` : (
            sp?.pipeGroupName ? `Mainline: ${sp.pipeGroupName}` : ''
          )
        ),
        mainlineId: sp?.id ?? sp?.productionJointId ?? null,
        sowId: sid ?? sp?.sowId ?? null,
        productionJointId: sp?.productionJointId ?? null,
        notes: snap.remarks != null && snap.remarks !== undefined ? String(snap.remarks) : '',
        dailyWeldLog,
        totalWelds: summaryAsNumber(s.totalWelds),
        acceptedCount: summaryAsNumber(s.acceptedCounts),
        rejectedCutout: summaryAsNumber(s.rejectedCutout),
        rejectedRepair: summaryAsNumber(s.rejectedRepair),
      };
    },
    getRemarksDraft: () => panelExposeSnapshotRef.current.remarks,
    refreshFromServer: async () => {
      const snap = panelExposeSnapshotRef.current;
      if (!snap.dprLogId) return false;
      const cacheKey = getCacheKey(snap.dprLogId, snap.shift);
      __dprProductivityCache.delete(cacheKey);
      __dprPipeTallyCache.delete(cacheKey);
      __dprPersonnelCache.delete(cacheKey);
      await snap.fetchProductivityData();
      await snap.fetchPipeTallyData();
      return true;
    },
    /** Re-run productivity GET without busting caches (use after first mainline refreshed, same project `dprLogId`). */
    reloadProductivityData: async () => {
      const snap = panelExposeSnapshotRef.current;
      if (!snap.dprLogId) return false;
      await snap.fetchProductivityData();
      await snap.fetchPipeTallyData();
      return true;
    },
  }), []);

  if (editingRow) {
    return (
      <EditWeldEntry
        key={editingRow?.id ?? 'weld-edit'}
        entry={editingRow}
        onSave={handleUpdateRow}
        onCancel={() => setEditingRow(null)}
        welderOptions={welderPersonnel}
        defectTypes={defectTypes}
        projectNdtMode={normalizedProjectNdtMethod}
        projectNdtMethods={projectNdtMethods}
        defaultWelderIds={defaultWelderIdsForNewWeldRows}
      />
    );
  }

  const showBlockingLoader = isLoadingWeldLog || isSaving || isLoadingPipeTally;

  return (
    <div style={{
      padding: 0,
      /* Keep a minimum box when embedded so the in-tab loader does not collapse to zero height */
      minHeight: compactChrome ? (showBlockingLoader ? 'min(420px, 70vh)' : 'auto') : '100vh',
      backgroundColor: '#F8FAFC',
      textAlign: 'left',
      position: 'relative',
    }}>
      {showBlockingLoader && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.75)',
            zIndex: 50,
            borderRadius: compactChrome ? '0 0 12px 12px' : 0,
          }}
        >
          <Spinner variant="inline" size="medium" />
        </div>
      )}
      <style>
        {`
        .productivity-input {
          background-color: #f9fafb !important;
          border-color: #e5e7eb !important;
          color: #111827 !important;
          border-radius: 6px !important;
        }
        
        .productivity-input:focus {
          border-color: #7c3aed !important;
          box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.2) !important;
          background-color: #fff !important;
        }

        .productivity-input::placeholder {
          color: #9ca3af !important;
        }
        
        .qty-suffix {
          font-weight: 500;
          font-size: 12px;
        }

        .rt-total-input {
          background-color: #ffffff !important;
          border-color: #e5e7eb !important;
          color: #111827 !important;
          font-weight: 600 !important;
          border-radius: 6px !important;
        }
        
        .rt-total-input input {
          color: #111827 !important;
          font-weight: 600 !important;
        }


        .weld-entry-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          background-color: white;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          overflow: hidden;
        }

        .weld-entry-table th {
          background-color: #EF4444;
          color: white;
          font-weight: 600;
          font-size: 11px;
          text-transform: uppercase;
          padding: 12px 16px;
          text-align: left;
          letter-spacing: 0.05em;
        }

        .weld-entry-table td {
          padding: 14px 16px;
          border-bottom: 1px solid #F3F4F6;
          background-color: white;
          font-size: 14px;
          color: #1F2937;
        }

        .weld-entry-table tbody tr:last-child td {
          border-bottom: none;
        }

        .weld-entry-table tbody tr:hover td {
          background-color: #F9FAFB;
        }

        .status-icon-circle {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justifyContent: center;
          cursor: pointer;
          transition: all 0.15s ease;
          border: 2px solid transparent;
        }

        .status-icon-circle:hover {
          transform: scale(1.08);
        }

        .status-icon-circle.accept {
          background-color: transparent;
          border-color: #10B981;
        }

        .status-icon-circle.accept.active {
          background-color: #10B981;
        }

        .status-icon-circle.indication {
          background-color: transparent;
          border-color: #F59E0B;
        }

        .status-icon-circle.indication.active {
          background-color: #F59E0B;
        }

        .status-icon-circle.reject {
          background-color: transparent;
          border-color: #EF4444;
        }

        .status-icon-circle.reject.active {
          background-color: #EF4444;
        }

        .action-icon-btn {
          cursor: pointer;
          color: #9CA3AF;
          transition: color 0.2s;
          padding: 4px;
          border-radius: 4px;
        }

        .action-icon-btn:hover {
          color: #EF4444;
          background-color: #FEE2E2;
        }
        `}
      </style>

      {/* Main Content Card */}
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        padding: '32px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        maxWidth: '100%'
      }}>
        {/* Header with Icon — Skip / Add Weld Details stay here for single-mainline; multi-mainline uses collapse header */}
        {!compactChrome && (
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '8px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                backgroundColor: '#F3F4F6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <BarChartOutlined style={{ color: '#4B5563', fontSize: '20px' }} />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{
                  margin: '0 0 4px 0',
                  color: '#0F172A',
                  fontSize: '16px',
                  fontWeight: 700,
                  textAlign: 'left'
                }}>
                  Productivity Log
                </h3>
                <p style={{
                  margin: 0,
                  color: '#64748B',
                  fontSize: '14px',
                  textAlign: 'left'
                }}>
                  Track daily production metrics and weld outputs
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <Checkbox
                  label="Skip Productivity"
                  checked={skipProductivity}
                  onChange={handleSkipProductivityChange}
                  borderColor="#00C9A7"
                />
                <CrcButton
                  label="Add Weld Details"
                  variant="primary"
                  size="small"
                  icon={Plus}
                  onClick={handleAddProductivity}
                  style={{ backgroundColor: '#F43F5E', borderColor: '#F43F5E' }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Summary counts — always visible, editable; placed above RT/AUT progress */}
        <div
          className="productivity-mainline-summary"
          style={{
            marginTop: compactChrome ? 0 : '8px',
            marginBottom: '24px',
            padding: '16px 20px',
            backgroundColor: '#F9FAFB',
            borderRadius: '8px',
            border: '1px solid #E5E7EB'
          }}
        >
          <h4
            style={{
              margin: '0 0 14px 0',
              fontSize: '13px',
              fontWeight: 600,
              color: '#374151',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              textAlign: 'left'
            }}
          >
            Summary
          </h4>
          <Row gutter={[16, 12]}>
            <Col xs={12} sm={6}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
                Total welds
              </div>
              <Input
                className="rt-total-input"
                inputMode="numeric"
                autoComplete="off"
                readOnly={false}
                disabled={false}
                value={summaryFieldInputs.totalWelds}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '');
                  setSummaryFieldInputs((s) => ({ ...s, totalWelds: v }));
                }}
                placeholder="0"
                size="large"
              />
            </Col>
            <Col xs={12} sm={6}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
                Accepted counts
              </div>
              <Input
                className="rt-total-input"
                inputMode="numeric"
                autoComplete="off"
                readOnly={false}
                disabled={false}
                value={summaryFieldInputs.acceptedCounts}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '');
                  setSummaryFieldInputs((s) => ({ ...s, acceptedCounts: v }));
                }}
                placeholder="0"
                size="large"
              />
            </Col>
            <Col xs={12} sm={6}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Scissors size={14} color="#64748B" aria-hidden />
                Rejected (Cutout)
              </div>
              <Input
                className="rt-total-input"
                inputMode="numeric"
                autoComplete="off"
                readOnly={false}
                disabled={false}
                value={summaryFieldInputs.rejectedCutout}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '');
                  setSummaryFieldInputs((s) => ({ ...s, rejectedCutout: v }));
                }}
                placeholder="0"
                size="large"
              />
            </Col>
            <Col xs={12} sm={6}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Wrench size={14} color="#64748B" aria-hidden />
                Rejected (Repair)
              </div>
              <Input
                className="rt-total-input"
                inputMode="numeric"
                autoComplete="off"
                readOnly={false}
                disabled={false}
                value={summaryFieldInputs.rejectedRepair}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '');
                  setSummaryFieldInputs((s) => ({ ...s, rejectedRepair: v }));
                }}
                placeholder="0"
                size="large"
              />
            </Col>
          </Row>
        </div>

        {/* NDT progress: RT, AUT — only when that method has weld rows (prevents duplicate/empty dashboards for RT+AUT projects). */}
        {visibleProgressTestTypes.length > 0 && (
        <div style={{ marginTop: '0', marginBottom: '32px', paddingBottom: '32px', borderBottom: '1px solid #E5E7EB' }}>
          {visibleProgressTestTypes.map((testType, index) => {
            const abbrev = String(testType.testType || 'NDT').toUpperCase();
            return (
              <div key={testType.testType || index} style={{ marginBottom: index < visibleProgressTestTypes.length - 1 ? '32px' : '0' }}>
                <h3 style={{
                  margin: '0 0 20px 0',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#64748B',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  {String(testType.testLabel || testType.testType).toUpperCase()} PROGRESS
                </h3>

                <Row gutter={24}>
                  <Col xs={24} sm={8}>
                    <div style={{ fontSize: '12px', fontWeight: 500, color: '#64748B', marginBottom: '8px' }}>
                      {`FIRST ${abbrev} WELD`}
                    </div>
                    <Input
                      value={testType.firstWeldNo || ''}
                      placeholder="e.g., W-001"
                      className="productivity-input"
                      size="large"
                      onChange={(e) =>
                        handleProgressTestTypeFieldChange(testType.testType, 'firstWeldNo', e.target.value)
                      }
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <div style={{ fontSize: '12px', fontWeight: 500, color: '#64748B', marginBottom: '8px' }}>
                      {`LAST ${abbrev} WELD`}
                    </div>
                    <Input
                      value={testType.lastWeldNo || ''}
                      placeholder="e.g., W-012"
                      className="productivity-input"
                      size="large"
                      onChange={(e) =>
                        handleProgressTestTypeFieldChange(testType.testType, 'lastWeldNo', e.target.value)
                      }
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <div style={{ fontSize: '12px', fontWeight: 500, color: '#64748B', marginBottom: '8px' }}>
                      {`TOTAL ${abbrev} ACCEPTED WELDS`}
                    </div>
                    <Input
                      value={
                        testType.totalCompleted !== undefined && testType.totalCompleted !== null
                          ? String(testType.totalCompleted)
                          : ''
                      }
                      className="rt-total-input"
                      size="large"
                      inputMode="numeric"
                      suffix={<span className="qty-suffix">Qty</span>}
                      style={{ textAlign: 'left', color: '#111827', fontWeight: '600' }}
                      onChange={(e) =>
                        handleProgressTestTypeFieldChange(
                          testType.testType,
                          'totalCompleted',
                          e.target.value
                        )
                      }
                    />
                  </Col>
                </Row>
              </div>
            );
          })}
        </div>
        )}

        {/* Weld Entry Table Section — RT / non-AUT in Daily Weld Log; AUT-only in AUT weld log */}
        {safeWeldEntryRows.length > 0 && (
          <div style={{ marginBottom: '32px', paddingBottom: '32px', borderBottom: '1px solid #E5E7EB' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '16px',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '4px',
                  height: '18px',
                  backgroundColor: '#EF4444',
                  borderRadius: '2px'
                }} />
                <h3 style={{
                  margin: 0,
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#1F2937',
                  textTransform: 'uppercase',
                  letterSpacing: '0.025em'
                }}>
                  Weld log
                </h3>
              </div>

              {(Array.isArray(weldEntryRows) ? weldEntryRows : []).some(r => r.selected) && (
                <CrcButton
                  label={`Delete Selected (${(Array.isArray(weldEntryRows) ? weldEntryRows : []).filter(r => r.selected).length})`}
                  variant="tertiary"
                  size="small"
                  icon={Trash2}
                  onClick={handleDeleteSelected}
                  style={{
                    color: '#EF4444',
                    borderColor: '#EF4444',
                    backgroundColor: '#FEF2F2'
                  }}
                />
              )}
            </div>

            {methodsWithWeldRows.map((methodCode, idx) => {
              const methodRows = weldLogRowsByMethod.get(methodCode) || [];
              const hasSelected = methodRows.some((r) => r.selected);
              const allSelected = methodRows.length > 0 && methodRows.every((r) => r.selected);
              const indeterminate = hasSelected && !allSelected;
              const afterPrevious = idx > 0;
              return (
                <div
                  key={`weld-section-${methodCode}`}
                  style={{
                    marginTop: afterPrevious ? '28px' : 0,
                    paddingTop: afterPrevious ? '24px' : 0,
                    borderTop: afterPrevious ? '1px solid #E5E7EB' : 'none'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                    {afterPrevious && (
                      <div style={{
                        width: '4px',
                        height: '16px',
                        backgroundColor: '#EF4444',
                        borderRadius: '2px'
                      }} />
                    )}
                    <h4 style={{
                      margin: 0,
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#1F2937',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em'
                    }}>
                      {methodCode} inspection
                    </h4>
                  </div>

                  {/* Status Legend with Icons */}
                  <div style={{ marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '12px', color: '#64748B' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <CheckCircle size={16} color="#10B981" fill="transparent" />
                      <span>Accepted (Yes)</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <AlertCircle size={16} color="#F59E0B" fill="transparent" />
                      <span>Accept w/ Indication</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Wrench size={16} color="#EF4444" strokeWidth={2} aria-hidden />
                      <span>Rejected (Repair)</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Scissors size={16} color="#EF4444" strokeWidth={2} aria-hidden />
                      <span>Rejected (Cutout)</span>
                    </div>
                  </div>

                  {/* Header Row */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    marginBottom: '16px',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB'
                  }}>
                    <div style={{ width: '60px', display: 'flex', justifyContent: 'center' }}>
                      <AntCheckbox
                        checked={allSelected}
                        indeterminate={indeterminate}
                        onChange={(e) => handleSelectAllByMethod(methodCode, e.target.checked)}
                      />
                    </div>
                    <div style={{ width: '20%', fontSize: '12px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      JOINT NO
                    </div>
                    <div style={{ width: '30%', fontSize: '12px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
                      {hasSelected ? (
                        <BulkStatusSelect onSelect={handleBulkStatusChange} />
                      ) : (
                        'STATUS'
                      )}
                    </div>
                    <div style={{ width: '30%', fontSize: '12px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {hasSelected ? (
                        <CustomWelderSelect
                          key={`bulk-welder-header-${methodCode}`}
                          placeholder="Set welder for selected"
                          value={bulkWelderPending}
                          options={welderPersonnel}
                          onChange={setBulkWelderPending}
                          onClose={(vals) => {
                            if (vals && vals.length > 0) {
                              handleBulkWelderIdChange(vals);
                            }
                            setBulkWelderPending([]);
                          }}
                        />
                      ) : (
                        'WELDER ID'
                      )}
                    </div>
                    <div style={{ width: '10%', fontSize: '12px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>
                      ACTION
                    </div>
                  </div>

                  {/* Card List */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {methodRows.map((row) => (
                  <div
                        key={`${methodCode}-${row.id}`}
                    onMouseEnter={() => setHoveredRow(row.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '16px',
                      border: hoveredRow === row.id ? '1px solid #00C9A7' : '1px solid #E5E7EB',
                      borderRadius: '8px',
                      backgroundColor: '#FFFFFF',
                      transition: 'border-color 0.2s ease'
                    }}
                  >
                    {/* Checkbox */}
                    <div style={{ width: '60px', display: 'flex', justifyContent: 'center' }}>
                      <AntCheckbox
                        checked={row.selected}
                        onChange={(e) => handleSelectRow(row.id, e.target.checked)}
                      />
                    </div>

                    {/* Joint Number */}
                    <div style={{ width: '20%', fontWeight: 600, color: '#111827', fontSize: '14px' }}>
                      {row.jointNo}
                    </div>

                    {/* Status Icons */}
                    <div style={{ width: '30%', display: 'flex', justifyContent: 'center' }}>
                      <div style={{ display: 'inline-flex', gap: '10px' }}>
                        <div
                          onClick={() => handleStatusChange(row.id, 'accept')}
                          title="Accept"
                          style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            opacity: (row.status && row.status !== 'accept') ? 0.3 : 1
                          }}
                        >
                          <CheckCircle
                            size={22}
                            color={row.status === 'accept' ? '#059669' : '#10B981'}
                            fill={row.status === 'accept' ? '#D1FAE5' : 'transparent'}
                          />
                        </div>
                        <div
                          onClick={() => handleStatusChange(row.id, 'indication')}
                          title="Accept with Indication"
                          style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            opacity: (row.status && row.status !== 'indication') ? 0.3 : 1
                          }}
                        >
                          <AlertCircle
                            size={22}
                            color={row.status === 'indication' ? '#D97706' : '#F59E0B'}
                            fill={row.status === 'indication' ? '#FEF3C7' : 'transparent'}
                          />
                        </div>
                        <div
                          onClick={() => handleStatusChange(row.id, 'reject_repair')}
                          title="Rejected (Repair)"
                          style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            opacity: (row.status && row.status !== 'reject_repair') ? 0.3 : 1
                          }}
                        >
                          <Wrench
                            size={22}
                            color={row.status === 'reject_repair' ? '#DC2626' : '#EF4444'}
                            strokeWidth={row.status === 'reject_repair' ? 2.6 : 2}
                            fill={row.status === 'reject_repair' ? '#FEE2E2' : 'transparent'}
                          />
                        </div>
                        <div
                          onClick={() => handleStatusChange(row.id, 'reject_cutout')}
                          title="Rejected (Cutout)"
                          style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            opacity: (row.status && row.status !== 'reject_cutout') ? 0.3 : 1
                          }}
                        >
                          <Scissors
                            size={22}
                            color={row.status === 'reject_cutout' ? '#DC2626' : '#EF4444'}
                            strokeWidth={row.status === 'reject_cutout' ? 2.6 : 2}
                            fill={row.status === 'reject_cutout' ? '#FEE2E2' : 'transparent'}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Welder ID Dropdown (Custom Multi-select) */}
                    <div style={{ width: '30%' }}>
                      <CustomWelderSelect
                        placeholder="Select Welders"
                        value={row.welderId}
                        options={welderPersonnel}
                        onChange={(vals) => handleWelderIdChange(row.id, vals)}
                      />
                    </div>

                    {/* Actions - Edit and Delete */}
                    <div style={{ width: '10%', display: 'flex', justifyContent: 'center', gap: '8px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          handleEditRow(row);
                        }}
                        title="Edit"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#D1D5DB',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#3B82F6';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#D1D5DB';
                        }}
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteRow(row.id)}
                        title="Delete"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#D1D5DB',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#EF4444';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#D1D5DB';
                        }}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Overall Comments - same CommentBox as Safety and Quality */}
        <div style={{ marginTop: '40px', marginBottom: '32px' }}>
          <CommentBox
            header="Overall Comments"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Add overall report notes or comments here..."
          />
        </div>

        {!hideFooterActions && (
          <div style={{
            position: 'sticky',
            bottom: '24px',
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '24px',
            paddingTop: '16px',
            zIndex: 10,
          }}>
            <CrcButton
              label={
                typeof isLastDprSection === 'boolean'
                  ? (isLastDprSection ? 'Save section' : 'Save & Next')
                  : 'Save Productivity Report'
              }
              variant="secondary"
              onClick={handleSave}
              disabled={isSubmitting || isSaving}
              style={{
                height: '42px',
                padding: '0 32px',
                fontSize: '14px',
                fontWeight: 600
              }}
              icon={Save}
            />
          </div>
        )}
      </div>

      {/* Upload Modal */}
      <Modal
        title="Upload Excel"
        open={isUploadModalVisible}
        onOk={handleUploadExcelFile}
        onCancel={handleUploadModalCancel}
        okText="Upload"
      >
        <Upload.Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <UploadOutlined style={{ color: '#7c3aed' }} />
          </p>
          <p className="ant-upload-text">Click or drag Excel file to this area</p>
          <p className="ant-upload-hint">Only .xls or .xlsx files are supported.</p>
        </Upload.Dragger>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        title={isBulkDelete ? "Delete Selected Entries" : "Delete Weld Entry"}
        message={isBulkDelete
          ? `Are you sure you want to delete these ${(Array.isArray(weldEntryRows) ? weldEntryRows : []).filter(r => r.selected).length} selected weld entries? This action cannot be undone.`
          : "Are you sure you want to delete this weld entry? This action cannot be undone."
        }
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isOpen={isDeleteModalOpen}
        size="medium"
        confirmButtonProps={{
          style: {
            backgroundColor: '#FF3B30',
            borderColor: '#FF3B30'
          }
        }}
      />
      <ConfirmationModal
        title="Skip Productivity"
        message={`Are you sure you want to skip the Productivity log for ${shift === 'night' ? 'night' : 'day'} shift?`}
        confirmText="Confirm"
        cancelText="Cancel"
        isOpen={isSkipConfirmOpen}
        onConfirm={handleConfirmSkipProductivity}
        onCancel={() => setIsSkipConfirmOpen(false)}
        size="medium"
      />

      {/* Add Productivity Modal */}
      {isAddModalVisible && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          paddingTop: '100px',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            width: '560px',
            maxWidth: '96vw',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                  Add Weld Details
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6B7280' }}>
                  Enter First Joint only for a single weld (e.g. j01), or add Last Joint for a range (e.g. j01 to j10).
                </p>
              </div>
              <button
                onClick={handleAddModalCancel}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9CA3AF',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {projectNdtMethods.length > 1 && (
                <div>
                  <div style={{
                    marginBottom: '12px',
                    paddingBottom: '8px',
                    borderBottom: '1px solid #E5E7EB',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#000000',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    NDT weld log
                  </div>
                  <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#6B7280', textAlign: 'left' }}>
                    Select the NDT type for this joint range.
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {projectNdtMethods.map((code) => {
                      const opt = { value: code, label: `${code} inspection` };
                      const active = newProductivity.ndtMethod === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setNewProductivity({ ...newProductivity, ndtMethod: opt.value })}
                          style={{
                            padding: '10px 14px',
                            borderRadius: '8px',
                            border: active ? '2px solid #3B82F6' : '1px solid #E5E7EB',
                            background: active ? '#EFF6FF' : '#fff',
                            color: active ? '#1D4ED8' : '#374151',
                            fontWeight: 600,
                            fontSize: '13px',
                            cursor: 'pointer',
                            textAlign: 'left'
                          }}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <div style={{
                  marginBottom: '12px',
                  paddingBottom: '8px',
                  borderBottom: '1px solid #E5E7EB',
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#000000',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  JOINT RANGE
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#374151',
                      textAlign: 'left'
                    }}>
                      First Joint
                    </label>
                    <input
                      type="text"
                      value={newProductivity.firstJoint}
                      onChange={(e) => {
                        setNewProductivity({ ...newProductivity, firstJoint: e.target.value });
                        setValidationError('');
                      }}
                      placeholder="e.g. j01"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: `1px solid ${validationError ? '#EF4444' : '#D1D5DB'}`,
                        fontSize: '14px',
                        outline: 'none',
                        color: '#1F2937',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = validationError ? '#EF4444' : '#4F46E5'}
                      onBlur={(e) => e.target.style.borderColor = validationError ? '#EF4444' : '#D1D5DB'}
                    />
                  </div>

                  <div style={{ paddingTop: '28px', color: '#9CA3AF' }}>
                    <ArrowRight size={20} />
                  </div>

                  <div style={{ flex: 1 }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#374151',
                      textAlign: 'left'
                    }}>
                      Last Joint (Opt)
                    </label>
                    <input
                      type="text"
                      value={newProductivity.lastJoint}
                      onChange={(e) => {
                        setNewProductivity({ ...newProductivity, lastJoint: e.target.value });
                        setValidationError('');
                      }}
                      placeholder="e.g. j10"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: `1px solid ${validationError ? '#EF4444' : '#D1D5DB'}`,
                        fontSize: '14px',
                        outline: 'none',
                        color: '#1F2937',
                        boxSizing: 'border-box'
                      }}
                      onFocus={(e) => e.target.style.borderColor = validationError ? '#EF4444' : '#4F46E5'}
                      onBlur={(e) => e.target.style.borderColor = validationError ? '#EF4444' : '#D1D5DB'}
                    />
                  </div>
                </div>

                {/* Validation Error */}
                {validationError && (
                  <div style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#EF4444',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <XCircle size={12} />
                    <span>{validationError}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              backgroundColor: '#ffffff',
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              marginTop: 'auto'
            }}>
              <CrcButton
                label="Cancel"
                variant="tertiary"
                onClick={handleAddModalCancel}
                size="medium"
                style={{ width: '48%', borderRadius: '24px', border: '1px solid #E5E7EB' }}
              />
              <CrcButton
                label="Add Records"
                variant="primary"
                onClick={handleAddRecords}
                size="medium"
                style={{
                  width: '48%',
                  backgroundColor: '#EF4444',
                  borderColor: '#EF4444',
                  borderRadius: '24px'
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

ProductivityMainlinePanel.displayName = 'ProductivityMainlinePanel';

const normalizeLoadedWeldStatus = (e) => {
  let s = e.status;
  if (s === 'reject') {
    if (e.repair) return 'reject_repair';
    if (e.rtCutOut || e.autCutOut || e.stn1CutOut) return 'reject_cutout';
    return 'reject_repair';
  }
  return s || 'accept';
};

const applyWeldStatusToEditForm = (prev, status) => {
  const isAut = isAutFamily(prev.ndtMethod);
  if (status === 'accept' || status === 'indication') {
    return {
      ...prev,
      status,
      repair: false,
      rtCutOut: false,
      autCutOut: false,
      stn1CutOut: false
    };
  }
  if (status === 'reject_repair') {
    return {
      ...prev,
      status,
      repair: true,
      rtCutOut: false,
      autCutOut: false,
      stn1CutOut: false
    };
  }
  if (status === 'reject_cutout') {
    return {
      ...prev,
      status,
      repair: false,
      rtCutOut: !isAut,
      autCutOut: isAut,
      stn1CutOut: false
    };
  }
  return { ...prev, status };
};

// EditWeldEntry Component
const EditWeldEntry = ({ entry, onSave, onCancel, welderOptions, defectTypes, projectNdtMode = 'RT', projectNdtMethods = null, defaultWelderIds = [] }) => {
  const initialNdtMethod = (() => {
    if (projectNdtMode === 'RT') return 'RT';
    if (projectNdtMode === 'AUT') return 'AUT';
    // Prefer entry method (supports RT/AUT/PAUT/MUT); fallback RT
    const e = String(entry.ndtMethod || '').trim().toUpperCase();
    return e || 'RT';
  })();

  /** RT vs AUT inspection toggles are stored separately so RT edits never overwrite AUT payload fields (and vice versa). */
  const getDefaultInspectionFields = () => {
    const isAut = isAutFamily(initialNdtMethod);
    const hasRt =
      entry.rtAccepted !== undefined ||
      entry.repairAccepted !== undefined ||
      entry.rtCutOut !== undefined;
    const hasAut =
      entry.autAccepted !== undefined ||
      entry.autRepairAccepted !== undefined ||
      entry.autCutOut !== undefined;
    if (isAut) {
      return {
        rtAccepted: false,
        autAccepted: hasAut
          ? !!entry.autAccepted
          : !!(entry.rtAccepted),
        repair: entry.repair || false,
        repairAccepted: false,
        autRepairAccepted: hasAut
          ? !!entry.autRepairAccepted
          : !!entry.repairAccepted,
        rtCutOut: false,
        autCutOut: hasAut ? !!entry.autCutOut : !!entry.rtCutOut
      };
    }
    if (hasRt) {
      return {
        rtAccepted: !!entry.rtAccepted,
        autAccepted: !!entry.autAccepted,
        repair: entry.repair || false,
        repairAccepted: !!entry.repairAccepted,
        autRepairAccepted: !!entry.autRepairAccepted,
        rtCutOut: !!entry.rtCutOut,
        autCutOut: !!entry.autCutOut
      };
    }
    return {
      rtAccepted: false,
      autAccepted: false,
      repair: false,
      repairAccepted: false,
      autRepairAccepted: false,
      rtCutOut: false,
      autCutOut: false
    };
  };

  const defaultInspectionFields = getDefaultInspectionFields();

  const initialWelderId = (() => {
    const w = entry.welderId;
    const hasWelder = Array.isArray(w)
      ? w.some((x) => welderIdFromUnknown(x) !== '')
      : welderIdFromUnknown(w) !== '';
    if (hasWelder) {
      return normalizeWelderIdsFromMetric(w);
    }
    if (Array.isArray(defaultWelderIds) && defaultWelderIds.length > 0) {
      return normalizeWelderIdsFromMetric(defaultWelderIds);
    }
    return [];
  })();

  const [formData, setFormData] = useState(() => {
    const normalizedStatus = normalizeLoadedWeldStatus(entry);
    const base = {
      ...entry,
      welderId: initialWelderId,
      repairWeld: entry.repairWeld || false,
      blowThrough: entry.blowThrough || false,
      stn1CutOut: entry.stn1CutOut || false,
      ndtMethod: initialNdtMethod,
      ...defaultInspectionFields,
      indications: (entry.indications && entry.indications.length > 0)
        ? entry.indications
        : [{ defectType: '', start: '', end: '', depth: '', length: '', height: '', defectAccepted: true }]
    };
    return applyWeldStatusToEditForm(base, normalizedStatus);
  });

  const [errors, setErrors] = useState({});

  const ndtMethod = String(formData.ndtMethod || '').trim().toUpperCase() || 'RT';
  const ndtInspectionShort = `${ndtMethod} inspection`;
  const ndtAccLabel = `${ndtMethod} Acc`;
  const ndtRepairAccLabel = `${ndtMethod} Repair accepted`;
  const ndtCutOutLabel = `${ndtMethod} Cut out`;
  const useAutInspectionFields = isAutFamily(ndtMethod);

  const toggleField = (field) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleIndicationChange = (index, field, value) => {
    let finalValue = value;

    // Strict numeric validation for location and dimension fields
    if (['start', 'end', 'depth', 'length'].includes(field)) {
      finalValue = value.replace(/[^0-9.]/g, ''); // Allow only digits and decimal point
      // Ensure only one decimal point
      const parts = finalValue.split('.');
      if (parts.length > 2) {
        finalValue = parts[0] + '.' + parts.slice(1).join('');
      }
    }

    const newIndications = [...(formData.indications || [])];
    const updated = { ...newIndications[index], [field]: finalValue };

    // Auto-calculate Length (mm) = End Location - Start Location when both are provided; clear Length if either is cleared
    if (field === 'start' || field === 'end') {
      const startVal = field === 'start' ? finalValue : (newIndications[index].start ?? '');
      const endVal = field === 'end' ? finalValue : (newIndications[index].end ?? '');
      const startNum = parseFloat(startVal);
      const endNum = parseFloat(endVal);
      const bothNumeric = !Number.isNaN(startNum) && !Number.isNaN(endNum) && startVal !== '' && endVal !== '';
      if (bothNumeric) {
        const lengthVal = endNum - startNum;
        updated.length = lengthVal >= 0 ? String(lengthVal) : String(lengthVal);
      } else {
        updated.length = '';
      }
    }

    newIndications[index] = updated;
    setFormData(prev => ({ ...prev, indications: newIndications }));
  };

  const addIndication = () => {
    setFormData(prev => ({
      ...prev,
      indications: [...(prev.indications || []), { defectType: '', start: '', end: '', depth: '', length: '', height: '', defectAccepted: true }]
    }));
  };

  const removeIndication = (index) => {
    setFormData(prev => ({
      ...prev,
      indications: (prev.indications || []).filter((_, i) => i !== index)
    }));
  };

  return (

    <div style={{ backgroundColor: '#F8FAFC', minHeight: '100vh', padding: '0px' }}>
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        padding: '32px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        maxWidth: '100%',
        minHeight: 'calc(100vh - 64px)',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          paddingBottom: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '4px', height: '24px', backgroundColor: '#EF4444', borderRadius: '2px' }} />
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#111827' }}>
              {formData.jointNo} - WELD LOG
            </h2>
          </div>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
            <X size={24} color="#6B7280" />
          </button>
        </div>

        {/* Identity & Location */}
        <div style={{ marginBottom: '32px', borderBottom: '1px solid #F3F4F6', paddingBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#374151', margin: '0 0 16px 0', textAlign: 'left' }}>• Identity & Location</h3>
          <div style={{ display: 'flex', gap: '54px', flexWrap: 'wrap' }}>
            <div style={{ flex: 2, minWidth: '300px' }}>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                fontSize: '14px',
                fontWeight: 500,
                color: '#374151',
                textAlign: 'left'
              }}>
                Joint No <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <input
                value={formData.jointNo}
                onChange={(e) => setFormData(prev => ({ ...prev, jointNo: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: '1px solid #D1D5DB',
                  fontSize: '14px',
                  outline: 'none',
                  color: '#1F2937',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                onBlur={(e) => e.target.style.borderColor = errors.jointNo ? '#EF4444' : '#D1D5DB'}
              />
              {errors.jointNo && <span style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.jointNo}</span>}
            </div>
            <div style={{ flex: 2, minWidth: '300px' }}>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                fontSize: '14px',
                fontWeight: 500,
                color: '#374151',
                textAlign: 'left'
              }}>
                Welder ID <span style={{ color: '#EF4444' }}> *</span>
              </label>
              <CustomWelderSelect
                placeholder="Select Welders"
                value={formData.welderId}
                options={welderOptions}
                onChange={(vals) => {
                  setFormData(prev => ({ ...prev, welderId: normalizeWelderIdsFromMetric(vals) }));
                  if (errors.welderId) setErrors(prev => ({ ...prev, welderId: null }));
                }}
              />
              {errors.welderId && <span style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.welderId}</span>}
            </div>
            <div style={{ flex: 1, minWidth: '150px' }}>
            </div>
          </div>
        </div>

        {/* Weld Status */}
        <div style={{ marginBottom: '32px', borderBottom: '1px solid #F3F4F6', paddingBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#374151', margin: '0 0 16px 0', textAlign: 'left' }}>• Weld Status <span style={{ color: 'red' }}>*</span></h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', backgroundColor: '#F3F4F6', borderRadius: '30px', padding: '4px', gap: '4px' }}>
            {[
              { id: 'accept', label: 'Accept', activeBg: '#14B8A6' },
              { id: 'indication', label: 'Accept w/ Indication', activeBg: '#F59E0B' },
              { id: 'reject_repair', label: 'Rejected (Repair)', activeBg: '#EF4444', Icon: Wrench },
              { id: 'reject_cutout', label: 'Rejected (Cutout)', activeBg: '#DC2626', Icon: Scissors }
            ].map((pill) => (
              <div
                key={pill.id}
                onClick={() => {
                  setFormData((prev) => applyWeldStatusToEditForm(prev, pill.id));
                }}
                style={{
                  flex: '1 1 120px',
                  textAlign: 'center',
                  padding: '10px 8px',
                  borderRadius: '26px',
                  cursor: 'pointer',
                  fontWeight: 500,
                  fontSize: '13px',
                  backgroundColor: formData.status === pill.id ? pill.activeBg : 'transparent',
                  color: formData.status === pill.id ? 'white' : '#6B7280',
                  boxShadow: formData.status === pill.id ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  {pill.Icon ? (
                    <pill.Icon
                      size={16}
                      color={formData.status === pill.id ? '#FFFFFF' : '#6B7280'}
                      strokeWidth={2}
                      aria-hidden
                    />
                  ) : null}
                  {pill.label}
                </span>
              </div>
            ))}
          </div>
          {errors.status && <span style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.status}</span>}
        </div>

        {/* Technical Evidence */}
        <div
          style={{
            marginBottom: '32px',
            borderBottom: '1px solid #F3F4F6',
            paddingBottom: '24px',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', width: '100%', justifyContent: 'flex-start' }}>
            {/* Icon or visual for Technical Evidence if needed, header style text */}
            <span style={{ fontSize: '14px', fontWeight: 600, color: '#9CA3AF' }}>Technical Evidence</span>
          </div>

          <div
            style={{
              marginBottom: '20px',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              alignSelf: 'stretch'
            }}
          >
            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#374151', margin: '0 0 12px 0', textAlign: 'left', width: '100%' }}>• Inspection method</h3>
            {Array.isArray(projectNdtMethods) && projectNdtMethods.length > 1 ? (
              <div style={{ display: 'flex', backgroundColor: '#F3F4F6', borderRadius: '30px', padding: '4px', width: '100%', maxWidth: '520px', marginBottom: '16px' }}>
                {projectNdtMethods.map((code) => {
                  const opt = { code, label: `${code} inspection` };
                  return (
                  <div
                    key={opt.code}
                    onClick={() => {
                      setFormData((prev) => ({
                        ...prev,
                        ndtMethod: opt.code
                      }));
                    }}
                    style={{
                      flex: 1,
                      textAlign: 'center',
                      padding: '10px 8px',
                      borderRadius: '26px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '13px',
                      backgroundColor: formData.ndtMethod === opt.code ? '#1D4ED8' : 'transparent',
                      color: formData.ndtMethod === opt.code ? 'white' : '#6B7280',
                      transition: 'all 0.2s'
                    }}
                  >
                    {opt.label}
                  </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <span
                  style={{
                    padding: '8px 14px',
                    borderRadius: '999px',
                    background: '#EFF6FF',
                    color: '#1D4ED8',
                    fontWeight: 700,
                    fontSize: '13px',
                  }}
                >
                  {ndtInspectionShort}
                </span>
              </div>
            )}
          </div>

          <div style={{ borderLeft: '3px solid #EF4444', paddingLeft: '12px', marginBottom: '16px' }}>
            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#1F2937', textAlign: 'left' }}>Weld Process Stats</h4>
          </div>

          <div style={{ display: 'flex', gap: '24px', marginBottom: '24px', flexWrap: 'wrap' }}>
            <ToggleField
              label="Blow Through"
              value={formData.blowThrough}
              onChange={() => toggleField('blowThrough')}
              activeColor="#EF4444"
              inactiveColor="#14B8A6"
            />
            <ToggleField
              label="Station 1 Cut Out"
              value={formData.stn1CutOut}
              onChange={() => toggleField('stn1CutOut')}
              activeColor="#EF4444"
              inactiveColor="#14B8A6"
            />
          </div>

          <div style={{ borderLeft: '3px solid #EF4444', paddingLeft: '12px', marginBottom: '16px' }}>
            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#1F2937', textAlign: 'left' }}>{ndtInspectionShort}</h4>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '16px' }}>
            <ToggleField
              label={ndtAccLabel}
              value={useAutInspectionFields ? !!formData.autAccepted : !!formData.rtAccepted}
              onChange={() => toggleField(useAutInspectionFields ? 'autAccepted' : 'rtAccepted')}
              inactiveColor="#EF4444"
            />
            <ToggleField label="Repair" value={formData.repair} onChange={() => toggleField('repair')} inactiveColor="#00C9A7" activeColor="#EF4444" />
            <ToggleField
              label={ndtRepairAccLabel}
              value={useAutInspectionFields ? !!formData.autRepairAccepted : !!formData.repairAccepted}
              onChange={() => toggleField(useAutInspectionFields ? 'autRepairAccepted' : 'repairAccepted')}
              inactiveColor="#EF4444"
            />
            <ToggleField
              label={ndtCutOutLabel}
              value={useAutInspectionFields ? !!formData.autCutOut : !!formData.rtCutOut}
              onChange={() => toggleField(useAutInspectionFields ? 'autCutOut' : 'rtCutOut')}
              inactiveColor="#00C9A7"
              activeColor="#EF4444"
            />
          </div>
          {errors.rtConsistency && <div style={{ color: '#EF4444', fontSize: '12px', marginTop: '8px' }}>{errors.rtConsistency}</div>}
        </div>

        {/* Indication Info */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#374151', margin: 0 }}>• Indication Info</h3>
            <CrcButton
              label="Add Indication"
              icon={Plus}
              variant="tertiary"
              size="small"
              onClick={addIndication}
              style={{ color: '#3B82F6', borderColor: '#3B82F6' }}
            />
          </div>

          {(formData.indications || []).map((indication, index) => (
            <div key={index} style={{ backgroundColor: '#F9FAFB', padding: '16px', borderRadius: '8px', marginBottom: '16px', position: 'relative' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ fontWeight: 600, fontSize: '14px', color: '#1F2937' }}>Indication #{index + 1}</span>
                <button onClick={() => removeIndication(index)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444' }}>
                  <Trash2 size={16} />
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                    textAlign: 'left'
                  }}>
                    Defect Type
                  </label>
                  <CustomDefectTypeSelect
                    value={indication.defectType}
                    options={defectTypes}
                    onChange={(val) => handleIndicationChange(index, 'defectType', val)}
                    placeholder="Select Defect Type"
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                    textAlign: 'left'
                  }}>
                    Start Location(mm)
                  </label>
                  <input
                    value={indication.start}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: '1px solid #D1D5DB',
                      fontSize: '14px',
                      outline: 'none',
                      color: '#1F2937',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                    onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                    onChange={(e) => handleIndicationChange(index, 'start', e.target.value)}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                    textAlign: 'left'
                  }}>
                    End Location(mm)
                  </label>
                  <input
                    value={indication.end}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: '1px solid #D1D5DB',
                      fontSize: '14px',
                      outline: 'none',
                      color: '#1F2937',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                    onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                    onChange={(e) => handleIndicationChange(index, 'end', e.target.value)}
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                    textAlign: 'left'
                  }}>
                    Depth (mm)
                  </label>
                  <input
                    value={indication.depth}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: '1px solid #D1D5DB',
                      fontSize: '14px',
                      outline: 'none',
                      color: '#1F2937',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                    onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                    onChange={(e) => handleIndicationChange(index, 'depth', e.target.value)}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                    textAlign: 'left'
                  }}>
                    Length (mm)
                  </label>
                  <input
                    value={indication.length}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: '1px solid #D1D5DB',
                      fontSize: '14px',
                      outline: 'none',
                      color: '#1F2937',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                    onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                    onChange={(e) => handleIndicationChange(index, 'length', e.target.value)}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                    textAlign: 'left'
                  }}>
                    Height (mm)
                  </label>
                  <input
                    value={indication.height || ''}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: '1px solid #D1D5DB',
                      fontSize: '14px',
                      outline: 'none',
                      color: '#1F2937',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                    onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.]/g, '');
                      handleIndicationChange(index, 'height', val);
                    }}
                  />
                </div>
              </div>

              {/* Defect Accepted Toggle */}
              <div style={{ marginTop: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  textAlign: 'left'
                }}>
                  Defect Accepted
                </label>
                <div style={{ display: 'flex', gap: '0' }}>
                  <button
                    type="button"
                    onClick={() => handleIndicationChange(index, 'defectAccepted', false)}
                    style={{
                      padding: '8px 24px',
                      fontSize: '14px',
                      fontWeight: 500,
                      border: '1px solid',
                      borderColor: indication.defectAccepted === false ? '#EF4444' : '#D1D5DB',
                      backgroundColor: indication.defectAccepted === false ? '#EF4444' : '#FFFFFF',
                      color: indication.defectAccepted === false ? '#FFFFFF' : '#374151',
                      borderRadius: '6px 0 0 6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    No
                  </button>
                  <button
                    type="button"
                    onClick={() => handleIndicationChange(index, 'defectAccepted', true)}
                    style={{
                      padding: '8px 24px',
                      fontSize: '14px',
                      fontWeight: 500,
                      border: '1px solid',
                      borderColor: indication.defectAccepted === true ? '#14B8A6' : '#D1D5DB',
                      backgroundColor: indication.defectAccepted === true ? '#14B8A6' : '#FFFFFF',
                      color: indication.defectAccepted === true ? '#FFFFFF' : '#374151',
                      borderRadius: '0 6px 6px 0',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      marginLeft: '-1px'
                    }}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Information */}
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#374151', margin: '0 0 16px 0', textAlign: 'left', textTransform: 'uppercase' }}>• Additional Information</h3>

          <div style={{ backgroundColor: '#F9FAFB', padding: '16px', borderRadius: '8px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '54px', marginBottom: '24px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  textAlign: 'left'
                }}>
                  Customer Comments
                </label>
                <input
                  value={formData.comments || ''}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, comments: e.target.value }));
                    if (errors.comments) setErrors(prev => ({ ...prev, comments: null }));
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: '1px solid #D1D5DB',
                    fontSize: '14px',
                    outline: 'none',
                    color: '#1F2937',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                  onBlur={(e) => e.target.style.borderColor = errors.comments ? '#EF4444' : '#D1D5DB'}
                />
                {errors.comments && <span style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.comments}</span>}
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  textAlign: 'left'
                }}>
                  CRCE Comment
                </label>
                <input
                  value={formData.crceComment || ''}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, crceComment: e.target.value }));
                    if (errors.crceComment) setErrors(prev => ({ ...prev, crceComment: null }));
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: '1px solid #D1D5DB',
                    fontSize: '14px',
                    outline: 'none',
                    color: '#1F2937',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                  onBlur={(e) => e.target.style.borderColor = errors.crceComment ? '#EF4444' : '#D1D5DB'}
                />
                {errors.crceComment && <span style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.crceComment}</span>}
              </div>
            </div>


            <CommentBox
              header="Repair Comment"
              value={formData.repairComment || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, repairComment: e.target.value }))}
              placeholder="Enter repair comments..."
            />
          </div>
        </div>





        {/* Footer Actions */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          padding: '24px 0',
          marginTop: 'auto',
          position: 'sticky',
          bottom: '0',
          backgroundColor: '#FFFFFF',
          zIndex: 20
        }}>
          <CrcButton
            label="Cancel"
            variant="tertiary"
            onClick={onCancel}
            style={{ height: '42px', padding: '0 24px', fontSize: '14px', fontWeight: 600 }}
          />
          <CrcButton
            label="Save"
            variant="secondary"
            onClick={() => {
              const newErrors = {};

              if (projectNdtMode === 'RT' && formData.ndtMethod === 'AUT') {
                message.error('This pipeline is RT-only; welds cannot be set to AUT.');
                return;
              }
              if (projectNdtMode === 'AUT' && formData.ndtMethod !== 'AUT') {
                message.error('This pipeline is AUT-only; welds cannot be set to RT.');
                return;
              }

              // Mandatory Fields
              if (!formData.jointNo) newErrors.jointNo = 'Joint No is required';
              if (!formData.status) newErrors.status = 'Weld Status is required';

              // No validation for toggle fields (Blow Through, Station 1 Cut Out, RT fields)

              if (Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
                message.error('Please resolve validation errors.');
                return;
              }

              // clear errors and save
              setErrors({});
              onSave(formData);
            }}
            style={{ height: '42px', padding: '0 32px', fontSize: '14px', fontWeight: 600 }}
            icon={Save}
          />
        </div>
      </div>
    </div>
  );
};

const ToggleField = ({ label, value, onChange, activeColor = '#14B8A6', inactiveColor = '#0F172A' }) => (
  <div style={{ flex: 1, minWidth: '200px' }}>
    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#6B7280', marginBottom: '8px', textAlign: 'left' }}>{label}</label>
    <div style={{ display: 'flex', border: '1px solid #E5E7EB', borderRadius: '24px', padding: '2px', backgroundColor: 'white' }}>
      <div
        onClick={() => value && onChange()}
        style={{
          flex: 1,
          textAlign: 'center',
          padding: '8px',
          borderRadius: '20px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500,
          backgroundColor: !value ? inactiveColor : 'transparent',
          color: !value ? 'white' : '#6B7280'
        }}
      >
        No
      </div>
      <div
        onClick={() => !value && onChange()}
        style={{
          flex: 1,
          textAlign: 'center',
          padding: '8px',
          borderRadius: '20px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500,
          backgroundColor: value ? activeColor : 'transparent',
          color: value ? 'white' : '#6B7280'
        }}
      >
        Yes
      </div>
    </div>
  </div>
);

// Local Component: CommentBox
const CommentBox = ({ header, value, onChange, placeholder }) => (
  <div style={{ marginTop: '0px' }}>
    <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 500, color: '#64748B', textAlign: 'left' }}>
      {header}
    </h4>
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={6}
      style={{
        width: '100%',
        padding: '16px',
        fontSize: '15px',
        border: '1px solid #E2E8F0',
        borderRadius: '12px',
        resize: 'vertical',
        outline: 'none',
        fontFamily: 'inherit',
        color: '#1F2937',
        backgroundColor: '#FFFFFF'
      }}
    />
  </div>
);

export default ProductivityMainlinePanel;
